import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../Pagination';
import Fab from '../../Fab';
import PeakToolbar, { type BarAction } from '../../PeakToolbar';
import { fmtDateTime, money, monthISO } from '../../../utils/region';
import { statutoryBookName } from '../../../utils/countryFeatures';
import {
  payrollRunsApi, PRR_MAX_DAYS, PRR_MAX_NOTE, PRR_MAX_TEXT, PRR_PAYMENT_STATUSES, PRR_RUN_STATUSES, PRR_RUN_TYPES, PRR_SUMMARY_KEYS, PrrRemitMonth,
  PrrAction, PrrAdvisory, PrrCalcJson, PrrCandidate, PrrCandidates, PrrChip, PrrCreateOptions, PrrEntryLine, PrrEvent, PrrItem, PrrLine, PrrLineDetail,
  PrrPage, PrrPostAdvisory, PrrPostPreview, PrrRefusal, PrrReversePreview, PrrRunBody, PrrRunList, PrrRunType, PrrSnapRow, PrrSnapshotPlan, PrrSsoMode, PrrSummaryBlock,
  PRR_BASIS_MOVE_WHYS, PrrBasisMove, PrrDependent,
} from '../../../utils/api/payrollRuns';
import { payrollSettingsApi, PRS_SEED_ITEM_CODES, PrsItem } from '../../../utils/api/payrollSettings';
import type { AwCtx } from '../ctx';
import { btnPrimary, card, dmy, errBox, inp, note, num, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, chip, chipOn, emptyLine, field, lbl } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';
import { DocEntriesBtn } from '../docEntries';
import { TF, fieldLabel, mmYyyy, runStatusLabel, runTypeLabel, ytdSourceLabel } from './payrollText';
import { PayDialog, PaymentsTab, PayRefusals, PayStatusChip } from './PayrollPayments';
import { RemitDialog, RemitMonthStrip, RemittanceDialog, RemittancesView } from './PayrollRemittances';
import DlBtn from '../../DlBtn';
import { payrollReportsApi, PRP_PAYSLIP_STATUSES } from '../../../utils/api/payrollReports';

// ── Payroll › Payroll List (batch 10 R6 — PAY-8 … PAY-11, PAY-17; design §3.2, §3.3, §4 R6) ─────────────────
// The client's payroll runs whose period ends inside the shell's period (R6-16), and one full-screen window per
// run: Draft → Calculated → Approved → Posted → Reversed, Return to draft, Void, Copy. The window holds the lines
// (one per employee: the frozen base salary, the amounts a person types — overtime, bonus, absence, deductions —
// and the SSO and PND.1 the server worked out), the four summary blocks with their three invariants, and the
// run's log.
// ⛔ The SERVER decides every figure and every action. Each action has ONE readiness answer (R6-1): GET returns it,
// the window draws from it, and the POST runs it again inside its own transaction. A button the readiness refuses
// is drawn disabled (Calculate, Approve) or not drawn at all (the folded actions), and the panel says why, each
// refusal with the jump that fixes it — the employee, the item in Payroll settings, the openings on the employee's
// Payroll tab, the run that holds a base salary. A refused non-GET pages the admins, so nothing is sent that the
// readiness already refused. The summary, the invariants and the "before posting" account advice are the
// server's; this file adds nothing up.
// ⛔ A read-only seat (books / auditor) gets no write control at all — never one shown disabled — and neither does
// a closed period or a filed month (the server's canEdit / lockReason; a filed month still offers Void when its
// readiness allows it, R6-23). The menu already hides this screen from a books login (menus.ts staffOnly).
// ⛔ The pane never writes ?menu / ?sub: every jump goes through ctx.setNav (which asks first when a cell holds
// typing) and clears this pane's ?run. It writes only its own ?run=<id> (replace), which opens the window.
// ⛔ Typed forms are Dialogs with `dirty` — X, Esc, the backdrop and Cancel ask the shell's one question. The run
// window is dirty while a cell holds text not yet committed (blur or Enter commits it, one write at a time, each
// carrying the run's row_version). The line window is read-only and nested, so Esc closes it alone (R6-7).
// ⛔ Writes queue, and each is decided when its turn comes — on the run as the last answer left it, never on the
// render it was clicked in: pressing Calculate takes the focus from a cell, whose blur commits it first, so the click
// that follows was made on a readiness that write is about to change. A queued action whose readiness that answer
// refuses is not sent; a cell committed twice before the first has landed changes the item the first one made.
// ⛔ Every typed value keeps to the server's bounds (PRR_MAX_TEXT / PRR_MAX_NOTE / PRR_MAX_DAYS, api/payrollRuns.ts).
// ⛔ Statutory names (ภ.ง.ด.1, สปส.1-10) are literals here, never inside a translation.
// ── R7 (PAY-12, PAY-13, PAY-17, PAY-18; design §3.4, §3.5, §4 R7; the R7 brief §3 package C) ────────────────────
// An approved run is posted, and a posted run reversed, by a person pressing Post or Reverse — nothing else here
// posts. Each has ONE plan on the server (services/payrollPost.ts): the window draws Post and Reverse from the run's
// readiness.post / .reverse (refused → Post drawn off and the panel names why, each refusal with its Fix; Reverse
// not drawn), and each dialog reads the same plan by GET (…/post-preview, …/reverse-preview — always 200, R7-1):
// the accrual's lines to the satang, what the month's ภ.ง.ด.1 and SSO rows become, the vouchers that may already
// carry this pay. Post is sent only with every voucher ticked and, where rows are replaced, the takeover ticked —
// so the POST is never one the server is certain to refuse (a refused non-GET pages the admins). A posted run is
// frozen (lockReason 'posted'); its entries are the house Entries window (docEntries.tsx, kind payroll_run), and a
// reversed run can only be copied to a new draft (the copy says which run it replaces).
// ⛔ The figures are the server's: this file adds no line up and works out no total.
// ── R8 (PAY-11's payment axis, PAY-15, PAY-16; design §3.3, §3.4.3, §3.4.4, §4 R8; the R8 brief §3 package C) ───────────
// A posted run is paid, and its month's ภ.ง.ด.1 and SSO remitted, by a person pressing Record payment or Remit —
// nothing else here pays. This file only wires them in (R8-21): the list's Payment column and payment chips (the server's
// derived status and counts, ?payment= on the list call), the Runs / Remittances switch (?prview=remittances — `view` is
// the shell's, R8-20) with the month strip above both views, ?remit=<id> opening a remittance; and in the run window the
// payment fact, Record payment (drawn only when readiness.pay says ok, never for a read-only seat), the Lines tab's Paid
// and Payment columns and the Payments tab. The dialogs themselves are PayrollPayments.tsx and PayrollRemittances.tsx.
// ── R9 (PAY-21; the R9 brief §3 package D task 3) ────────────────────────────────────────────────────────────────────────
// Payslips: the run window's Payslips (every live line, one PDF) and the line window's Payslip (that one person), both
// downloads that spin (DlBtn) through api/payrollReports.ts. Drawn exactly while the run's state has payslips
// (PRP_PAYSLIP_STATUSES: approved — "Not posted" on each slip — or posted) and, for one person, while the line is not
// removed — so the GET is never one the server refuses. A download is a read (R9-26): every seat that sees the run gets
// them. ⛔ Printed only: nothing here sends a payslip (no mail, no Telegram, no stamp — PQ18, PQ19).

const PND1 = 'ภ.ง.ด.1';
const SSO_FORM = 'สปส.1-10';
/** R8: the social-security remittance's short name, as the month strip and a remittance's log row write it. */
const SSO_SHORT = 'สปส.';
/** The group columns a line shows, and the item each one edits in place: a group cell is typed into only while the
 *  line holds at most one live item of that code and nothing else in the group (otherwise the line window does it). */
const CELLS: { group: string; groups: string[]; code: string }[] = [
  { group: 'overtime', groups: ['overtime'], code: 'OT' },
  { group: 'bonus_allowance', groups: ['bonus_allowance'], code: 'BONUS' },
  { group: 'other_earning', groups: ['other_earning'], code: 'OTHER_EARN' },
  { group: 'absence', groups: ['absence'], code: 'ABSENCE' },
  { group: 'deductions', groups: ['welfare_ee', 'other_deduction'], code: 'OTHER_DED' },
  { group: 'employer', groups: ['welfare_er', 'other_employer'], code: 'OTHER_ER' },
];
const ITEM_JUMP = new Set(['unanswered', 'item_unknown', 'welfare_off', 'item_account_missing', 'item_account_refused']);
/** R7: an account a Post needs (a role of Payroll settings), an employee's own expense account, and the runs a Post or a
 *  Reverse must wait for (R7-23, R7-24; and, R7 review, the run a copy replaces while that one is still posted —
 *  replaced_still_posted: reverse it first) — each a jump to where it is fixed. */
const ROLE_JUMP = new Set(['account_unmapped', 'account_refused']);
const ORDER_JUMP = new Set(['counted_unposted', 'dependent_runs', 'replaced_still_posted']);
/** Fixed by returning THIS run to draft: a moved basis, a lost payroll summary, and (R7 review) runs that count each
 *  other — a pair, or a ring of three or more (counted_cycle — calculated before Calculate kept the relation
 *  one-way): opening the other run would only lead round the ring back to this one, so its Fix is this run's
 *  Return, and none where Return is refused. */
const RETURN_FIX = new Set(['needs_recalc', 'supporting_doc_missing', 'counted_cycle']);
const YTD_JUMP = new Set(['ytd_gap', 'opening_after_run']);
const EMP_JUMP = new Set(['tier_p', 'tier_f', 'wht_condition_unsupported']);
const CLAIM_JUMP = new Set(['claim_overlap', 'claim_taken']);
const LINE_OPEN = new Set(['daily_days', 'base_off_cycle', 'negative', 'net_negative', 'adjust_invalid']);
const WELFARE_GROUPS = ['welfare_ee', 'welfare_er'];
/** The item types a person may put on a line, or change on one — the server's answerFor, mirrored: active in the
 *  client's list, never BASE (the line's own), and a welfare item only while the client's fund is on (R6-25). An item
 *  whose type has left this list since can only be removed (the server refuses any other write to it). */
const usableTypes = (types: PrsItem[], welfareOn: boolean): PrsItem[] =>
  types.filter(x => x.active && x.code !== 'BASE' && (welfareOn || !WELFARE_GROUPS.includes(x.summaryGroup)));
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 700, fontSize: 12 };
const statusPill = (s: string): React.CSSProperties =>
  s === 'approved' || s === 'posted' ? pillGreen : s === 'calculated' ? pillAmber : s === 'void' || s === 'reversed' ? pillRed : pillGrey;
const redBox: React.CSSProperties = { border: '1px solid #F3B4AE', background: '#FEF3F2' };
/** The run a refusal names by id (counted_unposted, dependent_runs, a held claim) — the first one, when the server sends it. */
const runIdOf = (r: PrrRefusal): string => String(r.runId || (r.runIds || [])[0] || '');
const docNosOf = (r: PrrRefusal): string => (r.docNos && r.docNos.length ? r.docNos : r.docNo ? [r.docNo] : []).join(', ');
/** An account rule (services/payrollAccounts.ts ACCOUNT_RULES) in Payroll settings' words. */
const ruleWords = (t: TF, rule?: string): string => (!rule ? '' : t(`prs_rule_${rule}`) !== `prs_rule_${rule}` ? t(`prs_rule_${rule}`) : t('prs_rule_other'));

const m2 = (sat: any): string => money((Number(sat) || 0) / 100);
const mOr = (sat: any): string => (sat === null || sat === undefined ? '—' : m2(sat));
const periodText = (from: string, to: string): string => `${dmy(from)} – ${dmy(to)}`;
const isIso = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
/** A typed amount in baht → satang: digits, at most two decimals, commas allowed; '' is 0. null = not a figure. */
function parseBaht(text: string): number | null {
  const s = String(text ?? '').replace(/,/g, '').trim();
  if (s === '') return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}
const satText = (sat: number): string => (sat ? (sat / 100).toFixed(2) : '');
const itemAmount = (i: PrrItem): number => (i.amountSat !== null && i.amountSat !== undefined ? Number(i.amountSat) : Math.round((Number(i.qty) || 0) * (Number(i.rateSat) || 0)));
const live = (l: PrrLine): PrrItem[] => (l.items || []).filter(i => !Number(i.removed));
const whoOf = (r: { employeeName?: string }) => String(r.employeeName || '');
/** The server's refusal of a write as a sentence of this screen. */
const prrErr = (e: any, t: TF): string => {
  if (e?.readOnlySeat) return String(e.message || '');
  const p = e?.payload || {};
  const code = String(p.code || '');
  const k = `prr_err_${code}`;
  // `ledger` carries the posting door's own sentence (PostError, English — the ledger's words, as the Journal shows them).
  if (code && t(k) !== k) return t(k, { docNo: String(p.docNo || ''), field: String(p.field || ''), message: String(p.message || p.error || '') });
  return String(e?.message || '') || t('prr_err_generic');
};
function useItemName() {
  const { t, i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return (code: string, stored?: { name?: string; nameEn?: string; nameTh?: string }): string =>
    ((PRS_SEED_ITEM_CODES as readonly string[]).includes(code) ? t(`prs_item_${code}`) : (thai ? stored?.nameTh || stored?.name || stored?.nameEn : stored?.nameEn || stored?.name || stored?.nameTh) || code);
}
const monthsText = (t: TF, y: number, ms?: number[]): string => (ms || []).map(m => mmYyyy(y, m)).join(', ') || t('prr_none_dash');
/** One refusal of a readiness answer: the words for its code, the people and items it names spelled out. */
function refusalText(t: TF, r: PrrRefusal, year: number, itemName: (c: string) => string): string {
  const k = `prr_ref_${r.code}`;
  const fields = [...(r.issues || []).map(i => `${fieldLabel(t, i.field)} (${t(`prl_issue_${i.code}`)})`), ...(r.issues?.length ? [] : (r.fields || (r.field ? [r.field] : [])).map(f => fieldLabel(t, f)))].join(', ');
  const opts = {
    form: PND1, item: r.itemCode ? itemName(r.itemCode) : '', missing: (r.missing || []).map(x => t(`prs_answer_${x}`, { form: PND1 })).join(' / '),
    months: monthsText(t, year, r.months), docNo: String(r.docNo || ''), kinds: (r.kinds || []).map(x => (x === 'pnd1' ? PND1 : t(`prr_kind_${x}`))).join(', '),
    tier: t(r.code === 'tier_f' ? 'prl_tier_f' : 'prl_tier_p'), fields: fields || t('prr_none_dash'),
    role: r.role ? t(`prs_role_${r.role}`, { form: PND1 }) : '', rule: ruleWords(t, r.rule), field: r.field ? t(`prr_acct_field_${r.field}`) : '', docNos: docNosOf(r),
  };
  return t(k) !== k ? t(k, opts) : t('prr_ref_other');
}
function chipText(t: TF, c: PrrChip, year: number): string {
  const k = `prr_chip_${c.code}`;
  return t(k) !== k ? t(k, { form: PND1, amount: m2(c.sat || 0), months: monthsText(t, year, c.months), reason: String(c.reason || '') }) : t('prr_chip_other');
}
/** Why a run cannot be changed (lockReason), why an employee is outside a run's criteria, and where an SSO figure came from. */
function lockText(t: TF, reason: string, status: string): string {
  if (reason === 'status') return t('prr_lock_status', { status: runStatusLabel(t, status) });
  const k = `prr_lock_${reason}`;
  return t(k) !== k ? t(k, { form: PND1 }) : t('prr_lock_other');
}
/** An amber note beside a Post or a Reverse (filing_edited names the filing and the figure typed over it). */
function postAdvText(t: TF, a: PrrPostAdvisory): string {
  const k = `prr_padv_${a.code}`;
  return t(k) !== k ? t(k, { form: a.kind === 'sso' ? SSO_FORM : PND1, amount: money(Number(a.taxAmount) || 0) }) : t('prr_padv_other');
}
function whyText(t: TF, c: { why?: string; docNo?: string }): string {
  const k = `prr_why_${c.why}`;
  return t(k) !== k ? t(k, { docNo: String(c.docNo || '') }) : t('prr_why_other');
}
function ssoSourceText(t: TF, source: string, reason: string): string {
  const k = `prr_sso_src_${source}`;
  return t(k) !== k ? t(k, { reason }) : String(source || '');
}
function whtSourceText(t: TF, source: string): string {
  const k = `prr_wht_src_${source}`;
  return t(k) !== k ? t(k, {}) : String(source || '');
}
const Chips = ({ chips, year, t }: { chips: PrrChip[]; year: number; t: TF }) => (
  <>{(chips || []).map((c, i) => (
    <span key={`${c.code}:${i}`} style={{ ...(c.code === 'projected_pending' ? pillGrey : pillAmber), marginRight: 4, marginTop: 3 }} data-chip={c.code}>{chipText(t, c, year)}</span>
  ))}</>
);

// ── The list ─────────────────────────────────────────────────────────────────────────────────────────
export function PayrollRunsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [payment, setPayment] = useState('');
  const [sort, setSort] = useState('');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrrRunList | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [creating, setCreating] = useState(false);
  // ⛔ A read-only seat, and a closed period, get no write control at all (never one shown disabled).
  const readOnly = isReadOnlySeat(ctx);
  const canCreate = !readOnly && !ctx.periodClosed;
  const openId = params.get('run') || '';
  // R8: the Payroll List's own view switch (?prview=remittances; R8-20) and the remittance open on it (?remit=<id>, which
  // implies the Remittances view). ?run=<id> opens the run window in either view.
  const remitId = params.get('remit') || '';
  const view: 'runs' | 'remittances' = params.get('prview') === 'remittances' || remitId ? 'remittances' : 'runs';
  const [remitting, setRemitting] = useState<PrrRemitMonth | null>(null);
  // R8 review L2: closing Remit reads the month strip again (the card may have shown it a figure the strip had not).
  const [stripKey, setStripKey] = useState(0);
  useEffect(() => {
    let alive = true;
    payrollRunsApi.list(ctx.clientId, { type, status, showVoid: status === 'void', sort, dir: sort ? dir : undefined, page, periodId: ctx.periodId, payment })
      .then(d => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData(null); setErr(prrErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, reload, type, status, payment, sort, dir, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ?run=<id> opens that run's window (the deep link colleagues hand each other); closing drops it.
  const setOpen = (id: string) => setParams(p => { const n = new URLSearchParams(p); if (id) n.set('run', id); else n.delete('run'); return n; }, { replace: true });
  const setView = (v: 'runs' | 'remittances') => setParams(p => { const n = new URLSearchParams(p); if (v === 'remittances') n.set('prview', v); else { n.delete('prview'); n.delete('remit'); } return n; }, { replace: true });
  const setRemit = (id: string) => setParams(p => { const n = new URLSearchParams(p); if (id) { n.set('remit', id); n.set('prview', 'remittances'); } else n.delete('remit'); return n; }, { replace: true });
  const refresh = () => setReload(n => n + 1);
  const sortBy = (k: string) => { if (sort === k) setDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSort(k); setDir('asc'); } setPage(1); };
  const byType = data?.counts?.byType || {};
  const byStatus = data?.counts?.byStatus || {};
  const byPayment: Record<string, number> = data?.counts?.byPayment || {};
  // Payment chips: the payment states the period's posted runs actually hold (and the one chosen) — the server's counts.
  const payments = PRR_PAYMENT_STATUSES.filter(s => (byPayment[s] || 0) > 0 || s === payment);
  // A month can have something to remit only once a run of the period has been posted (a reversed one keeps its month's
  // remittances in the record): without one the strip reads nothing.
  const anyPosted = (byStatus.posted || 0) + (byStatus.reversed || 0) > 0;
  // Status chips: the states this period actually holds (and the one chosen) — a filter that could only ever be
  // empty is not shown (R6-14). Void runs are left out of the list unless Void is chosen.
  const statuses = PRR_RUN_STATUSES.filter(s => (byStatus[s] || 0) > 0 || s === status);
  const filters = (
    <>
      {(['', ...PRR_RUN_TYPES] as string[]).map(v => (
        <button key={v || 'all'} data-type-chip={v || 'all'} aria-pressed={v === type} onClick={() => { setType(v); setPage(1); }}
          style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(v === type ? chipOn : {}) }}>
          {v ? runTypeLabel(t, v) : t('prr_view_all')}{v && byType[v] !== undefined ? ` · ${byType[v]}` : ''}
        </button>
      ))}
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', flex: 'none' }} aria-hidden />
      <button data-status-chip="all" aria-pressed={!status} onClick={() => { setStatus(''); setPage(1); }}
        style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(!status ? chipOn : {}) }}>{t('prr_status_all')}</button>
      {statuses.map(s => (
        <button key={s} data-status-chip={s} aria-pressed={s === status} onClick={() => { setStatus(s); setPage(1); }}
          style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(s === status ? chipOn : {}) }}>
          {runStatusLabel(t, s)} · {byStatus[s] || 0}
        </button>
      ))}
      {payments.length > 0 && <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', flex: 'none' }} aria-hidden />}
      {payments.map(s => (
        <button key={s} data-payment-chip={s} aria-pressed={s === payment} onClick={() => { setPayment(s === payment ? '' : s); setPage(1); }}
          style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(s === payment ? chipOn : {}) }}>
          {t(`prr_pay_status_${s}`)} · {byPayment[s] || 0}
        </button>
      ))}
    </>
  );
  const head = (k: string, label: string, right = false) => (
    <th style={{ ...th, ...(right ? { textAlign: 'right' } : {}), cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => sortBy(k)} data-sort={k} aria-sort={sort === k ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      {label}{sort === k ? <i className={`ti ti-chevron-${dir === 'asc' ? 'up' : 'down'}`} aria-hidden style={{ marginLeft: 3 }} /> : null}
    </th>
  );
  const rows = data?.rows || [];
  const viewChip = (v: 'runs' | 'remittances', label: string) => (
    <button data-prview={v} aria-pressed={v === view} onClick={() => setView(v)} style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(v === view ? chipOn : {}) }}>{label}</button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-pane="payroll-runs" data-view={view}>
      {err && <div style={errBox} data-runs-error>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }} data-prview-switch>{viewChip('runs', t('prr_view_runs'))}{viewChip('remittances', t('prr_view_remittances'))}</div>
      <RemitMonthStrip ctx={ctx} active={anyPosted} readOnly={readOnly} reloadKey={reload + stripKey} onRemit={setRemitting} />
      {view === 'remittances' && <RemittancesView ctx={ctx} reloadKey={reload} onOpen={setRemit} />}
      {view === 'runs' && <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar filters={filters} right={<span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }} data-count>{data ? t('prr_n_runs', { count: data.total }) : ''}</span>} />
        <div style={{ overflowX: 'auto' }}>
          {data === null ? (err ? null : <Loader />) : !rows.length ? <div style={emptyLine} data-runs-empty>{t('prr_none')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  {head('docNo', t('prr_col_doc_no'))}{head('period', t('prr_col_period'))}{head('runType', t('prr_col_type'))}{head('payDate', t('prr_col_pay_date'))}
                  {head('employees', t('prr_col_employees'), true)}{head('gross', t('prr_col_gross'), true)}{head('employerCost', t('prr_col_employer_cost'), true)}
                  {head('net', t('prr_col_net'), true)}{head('status', t('prr_col_status'))}<th style={th}>{t('prr_col_payment')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-run-row={r.id} data-status={r.status} style={r.status === 'void' ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : undefined}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => setOpen(r.id)} data-open-run={r.id} style={{ ...linkBtn, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{r.docNo}</button>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{periodText(r.periodFrom, r.periodTo)}</td>
                    <td style={td}>{runTypeLabel(t, r.runType)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.payDate ? dmy(r.payDate) : '—'}</td>
                    <td style={num}>{r.employees}</td>
                    <td style={num}>{m2(r.grossSat)}</td>
                    <td style={num}>{m2(r.employerCostSat)}</td>
                    <td style={num}>{m2(r.netSat)}</td>
                    <td style={td}><span style={statusPill(r.status)} data-run-status={r.status}>{runStatusLabel(t, r.status)}</span></td>
                    <td style={{ ...td, color: 'var(--text-tertiary)' }} data-payment={r.paymentStatus || ''}>{r.paymentStatus ? <PayStatusChip status={r.paymentStatus} t={t} /> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>}
      {view === 'runs' && data && <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />}
      {view === 'runs' && <div style={note}>{t('prr_list_note', { form: PND1 })}</div>}
      {canCreate && view === 'runs' && <Fab onClick={() => setCreating(true)} title={t('prr_new_title')} />}
      {remitting && !readOnly && <RemitDialog ctx={ctx} month={remitting} onClose={() => { setRemitting(null); setStripKey(n => n + 1); }} onDone={() => { setRemitting(null); refresh(); }} />}
      {remitId && <RemittanceDialog key={remitId} ctx={ctx} remitId={remitId} readOnly={readOnly} onClose={() => setRemit('')} onReversed={refresh} />}
      {creating && canCreate && (
        <CreateRunDialog ctx={ctx} onClose={() => setCreating(false)} onCreated={id => { setCreating(false); refresh(); setOpen(id); }} />
      )}
      {openId && !creating && <RunWindow key={openId} ctx={ctx} runId={openId} readOnly={readOnly} onClose={() => setOpen('')} onChanged={refresh} onOpenRun={setOpen} />}
    </div>
  );
}

// ── Create (a small form) ────────────────────────────────────────────────────────────────────────────
// Month (inside the shell's period), type, the period itself (the whole month for a monthly run — R6-2; any range
// inside the month for a daily one), the document and payment dates, the social-security option and a note. Every
// change asks GET /runs/create-options what Create would do; Create stays off while that answer names a refusal
// (a filed month, a closed period, no open period) or "Not calculated" has no reason — it never POSTs one.
function monthsOf(ctx: AwCtx): string[] {
  const from = String(ctx.period?.start_date || '').slice(0, 7), to = String(ctx.period?.end_date || '').slice(0, 7);
  const out: string[] = [];
  if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to)) return out;
  let [y, m] = from.split('-').map(Number);
  while (`${y}-${String(m).padStart(2, '0')}` <= to && out.length < 36) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}
function CreateRunDialog({ ctx, onClose, onCreated }: { ctx: AwCtx; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const months = useMemo(() => monthsOf(ctx), [ctx]);
  const [ym, setYm] = useState(() => (months.includes(monthISO()) ? monthISO() : months[months.length - 1] || monthISO()));
  const [type, setType] = useState<PrrRunType>('monthly');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [docDate, setDocDate] = useState('');
  const [payDate, setPayDate] = useState('');
  const [ssoMode, setSsoMode] = useState<PrrSsoMode>('month_rate');
  const [ssoOffReason, setSsoOffReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [opts, setOpts] = useState<{ key: string; o: PrrCreateOptions } | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T, k = '') => { setDirty(true); if (k) setTouched(x => ({ ...x, [k]: true })); set(v); };
  const key = `${ym}|${type}`;
  useEffect(() => {
    let alive = true;
    setOpts(null);
    payrollRunsApi.createOptions(ctx.clientId, ym, type).then(o => { if (alive) { setOpts({ key: `${ym}|${type}`, o }); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ym, type]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The server's defaults for this month, applied to what the person has not typed (a monthly period is never typed).
  const o = opts && opts.key === key ? opts.o : null;
  useEffect(() => {
    if (!o) return;
    if (type === 'monthly' || !touched.period) { setPeriodFrom(o.periodFrom); setPeriodTo(o.periodTo); }
    if (!touched.docDate) setDocDate(o.docDate);
    if (!touched.payDate) setPayDate(o.payDate);
  }, [o]);   // eslint-disable-line react-hooks/exhaustive-deps
  const inMonth = (d: string) => isIso(d) && d.slice(0, 7) === ym;
  const periodOk = type === 'monthly' ? !!o && periodFrom === o.periodFrom && periodTo === o.periodTo : inMonth(periodFrom) && inMonth(periodTo) && periodFrom <= periodTo;
  const reasonMissing = ssoMode === 'off' && !ssoOffReason.trim();
  const ready = !!o && !o.refusals.length && periodOk && isIso(docDate) && isIso(payDate) && !reasonMissing;
  const rate = o?.ssoOptions.find(x => x.mode === 'month_rate');
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const [y, m] = ym.split('-').map(Number);
    try {
      const r = await payrollRunsApi.create(ctx.clientId, {
        year: y, month: m, runType: type, periodFrom, periodTo, docDate, payDate, ssoMode,
        ...(ssoMode === 'off' ? { ssoOffReason: ssoOffReason.trim() } : {}), ...(noteText.trim() ? { note: noteText.trim() } : {}),
      });
      setDirty(false); onCreated(String(r.run.id));
    } catch (e: any) { setErr(prrErr(e, t)); } finally { setBusy(false); }
  };
  const [yy, mm] = ym.split('-').map(Number);
  return (
    <Dialog width={600} title={t('prr_create_title')} docNo={o?.docNoPreview || undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-plus" label={t('prr_create')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-create-form>
        {err && <div style={errBox} data-create-error>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label htmlFor="prr-cr-month" style={lbl}>{t('prr_f_month')}</label>
            <select id="prr-cr-month" value={ym} onChange={e => ed(setYm, e.target.value)} style={field} data-cr="month">
              {months.map(x => <option key={x} value={x}>{mmYyyy(Number(x.slice(0, 4)), Number(x.slice(5)))}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="prr-cr-type" style={lbl}>{t('prr_f_type')}</label>
            <select id="prr-cr-type" value={type} onChange={e => ed(setType, e.target.value as PrrRunType)} style={field} data-cr="type">
              {PRR_RUN_TYPES.map(x => <option key={x} value={x}>{runTypeLabel(t, x)}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={lbl}>{t('prr_f_period')}</label>
          {type === 'monthly' ? (
            <div style={{ fontSize: 12.5 }} data-cr-period>{o ? periodText(o.periodFrom, o.periodTo) : '…'} <span style={{ color: 'var(--text-tertiary)' }}>· {t('prr_period_whole_month')}</span></div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" value={periodFrom} onChange={e => ed(setPeriodFrom, e.target.value, 'period')} style={field} aria-label={t('prr_f_period_from')} data-cr="period_from" />
              <span>–</span>
              <input type="date" value={periodTo} onChange={e => ed(setPeriodTo, e.target.value, 'period')} style={field} aria-label={t('prr_f_period_to')} data-cr="period_to" />
            </div>
          )}
          {type !== 'monthly' && !periodOk && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-cr-period-bad>{t('prr_period_inside_month', { month: mmYyyy(yy, mm) })}</div>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label htmlFor="prr-cr-doc" style={lbl}>{t('prr_f_doc_date')}</label>
            <input id="prr-cr-doc" type="date" value={docDate} onChange={e => ed(setDocDate, e.target.value, 'docDate')} style={field} data-cr="doc_date" />
          </div>
          <div>
            <label htmlFor="prr-cr-pay" style={lbl}>{t('prr_f_pay_date')}</label>
            <input id="prr-cr-pay" type="date" value={payDate} onChange={e => ed(setPayDate, e.target.value, 'payDate')} style={field} data-cr="pay_date" />
          </div>
        </div>
        <div>
          <label htmlFor="prr-cr-sso" style={lbl}>{t('prr_f_sso')}</label>
          <select id="prr-cr-sso" value={ssoMode} onChange={e => ed(setSsoMode, e.target.value as PrrSsoMode)} style={field} data-cr="sso_mode">
            <option value="month_rate">{rate ? t('prr_sso_opt_rate', { rate: rate.rate, cap: money(rate.cap ?? 0) }) : t('prr_sso_mode_month_rate')}</option>
            <option value="off">{t('prr_sso_opt_off')}</option>
          </select>
          {ssoMode === 'off' && (
            <textarea value={ssoOffReason} onChange={e => ed(setSsoOffReason, e.target.value)} rows={2} placeholder={t('prr_sso_off_reason_ph')} maxLength={PRR_MAX_TEXT}
              style={{ ...field, marginTop: 6, ...(reasonMissing ? amberBox : {}) }} data-cr="sso_off_reason" aria-label={t('prr_f_sso_off_reason')} />
          )}
        </div>
        <div>
          <label htmlFor="prr-cr-note" style={lbl}>{t('prr_f_note')}</label>
          <input id="prr-cr-note" value={noteText} onChange={e => ed(setNoteText, e.target.value)} maxLength={PRR_MAX_NOTE} style={field} data-cr="note" />
        </div>
        {o && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }} data-cr-candidates={o.candidates}>
            {type === 'off_cycle' ? t('prr_cr_off_cycle_empty') : t('prr_cr_candidates', { count: o.candidates })}
          </div>
        )}
        {o && o.refusals.length > 0 && (
          <div style={{ ...card, ...amberBox, display: 'flex', flexDirection: 'column', gap: 4 }} data-cr-refusals>
            {o.refusals.map((r, i) => (
              <div key={`${r.code}:${i}`} style={{ fontSize: 12.5, color: '#92400E' }} data-cr-refusal={r.code}>
                <i className="ti ti-alert-triangle" aria-hidden /> {refusalText(t, r, yy, c => c)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── The run window (full screen) ─────────────────────────────────────────────────────────────────────
type Win = { kind: 'detail'; lineId: string } | { kind: 'reason'; act: 'return' | 'void' | 'remove'; lineId?: string; name?: string } | { kind: 'add' }
  | { kind: 'post' } | { kind: 'reverse' } | { kind: 'pay' } | null;
/** How a queued write ended: sent (r = the answer) or refused (err, and the server's `code`); `held` = never sent,
 *  because when its turn came the readiness the last answer gave no longer allowed it (err says so). */
type Done = { ok: boolean; err?: string; code?: string; r?: any; held?: boolean };
type Send = (rowVersion: number) => Promise<any>;
/** What a queued write sends, decided when its turn comes from the run as the last answer left it — or null: nothing
 *  (left) to send. */
type Plan = (b: PrrRunBody) => Send | null | Promise<Send | null>;
/** One write on the run: `need` names the readiness it runs under (the edit, unless it is one of the actions). */
type Writer = (fn: Send, need?: PrrAction) => Promise<Done>;
type Tab = 'lines' | 'summary' | 'payments' | 'log';
function RunWindow({ ctx, runId, readOnly, onClose, onChanged, onOpenRun }: {
  ctx: AwCtx; runId: string; readOnly: boolean; onClose: () => void; onChanged: () => void; onOpenRun: (id: string) => void;
}) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const [body, setBody] = useState<PrrRunBody | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<Tab>('lines');
  const [linePage, setLinePage] = useState(1);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // A committed cell (or header field) keeps its text on the glass, marked as saving, until the write carrying it has
  // landed and the run been read again — so it never seems to vanish and get typed a second time (`n` = which commit).
  const [sending, setSending] = useState<Record<string, { v: string; n: number }>>({});
  const commits = useRef(0);
  const [cellErr, setCellErr] = useState<Record<string, string>>({});
  const [busyAct, setBusyAct] = useState('');
  const [saving, setSaving] = useState(0);
  const [win, setWin] = useState<Win>(null);
  const [types, setTypes] = useState<PrsItem[] | null>(null);
  const [welfareOn, setWelfareOn] = useState(false);
  const [logKey, setLogKey] = useState(0);
  // R8: bumped when a payment is recorded or reversed, so the Payments tab reads its list again.
  const [payKey, setPayKey] = useState(0);
  // FS-12: the EDIT PATH sets it (a cell or a header field holding typing) and nothing else does.
  const [dirty, setDirty] = useState(false);
  const bodyRef = useRef<PrrRunBody | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const load = useCallback(async () => {
    try { const b = await payrollRunsApi.get(ctx.clientId, runId, linePage); bodyRef.current = b; setBody(b); setErr(''); }
    catch (e: any) { setErr(prrErr(e, t)); }
  }, [ctx.clientId, runId, linePage]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const writable = !!body && !readOnly && body.canEdit && !!body.readiness?.edit?.ok;
  // The item types this client may put on a line (R5's settings, read only when the window can write).
  useEffect(() => {
    if (!writable || types) return;
    let alive = true;
    (async () => {
      const all: PrsItem[] = [];
      for (let p = 1; p <= 20; p++) { const d = await payrollSettingsApi.items(ctx.clientId, 'client', p); all.push(...d.rows); if (all.length >= d.total || !d.rows.length) break; }
      const s = await payrollSettingsApi.get(ctx.clientId);
      if (alive) { setTypes(all); setWelfareOn(!!s.general?.welfareEnabled); }
    })().catch(() => { if (alive) setTypes([]); });
    return () => { alive = false; };
  }, [writable, types, ctx.clientId]);
  /** One write at a time, each with the row_version the last answer gave, the run read again after it. `saving` counts
   *  from the moment a write is queued, so Calculate and Approve are drawn off while one waits. When its turn comes the
   *  readiness that answer gave for `need` is read again: refused, nothing is sent (held) and the panel says why. */
  const queueWrite = (plan: Plan, need: PrrAction): Promise<Done> => {
    setSaving(n => n + 1);
    const p = queue.current.then(async (): Promise<Done> => {
      let sent = false;
      try {
        const b = bodyRef.current;
        if (!b || !b.readiness?.[need]?.ok) return { ok: false, held: true, err: t('prr_held') };
        const send = await plan(b);
        if (!send) return { ok: true };
        sent = true;
        return { ok: true, r: await send(Number(b.run.rowVersion ?? 0)) };
      } catch (e: any) { sent = true; return { ok: false, err: prrErr(e, t), code: String(e?.payload?.code || '') }; }
      finally {
        if (sent) { await load(); setLogKey(n => n + 1); onChanged(); }
        setSaving(n => n - 1);
      }
    });
    queue.current = p;
    return p;
  };
  const write: Writer = (fn, need = 'edit') => queueWrite(() => fn, need);
  const setLine = (key: string, v: string) => { setDirty(true); setCellErr(x => { const n = { ...x }; delete n[key]; return n; }); setDrafts(d => ({ ...d, [key]: v })); };
  const takeDraft = (key: string): string | undefined => {
    const v = drafts[key];
    if (v === undefined) return undefined;
    setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
    if (Object.keys(drafts).every(k => k === key)) setDirty(false);
    return v;
  };
  // A refused write puts its text back in the cell (unless the person has typed there again since) with the reason.
  const failCell = (key: string, v: string, msg: string) => { setDirty(true); setDrafts(d => (d[key] !== undefined ? d : { ...d, [key]: v })); setCellErr(x => ({ ...x, [key]: msg })); };
  /** A typed field committed: its text shown as saving until the write lands; what that write sends is `plan`'s to
   *  decide when its turn comes (a second commit of the cell then patches the item the first one added). */
  const commitField = async (key: string, v: string, plan: Plan): Promise<void> => {
    const n = ++commits.current;
    setSending(s => ({ ...s, [key]: { v, n } }));
    const res = await queueWrite(plan, 'edit');
    setSending(s => { if (s[key]?.n !== n) return s; const x = { ...s }; delete x[key]; return x; });
    if (!res.ok) failCell(key, v, res.err || '');
  };
  /** A line as the last answer gave it — off the page that answer holds, else read on its own (a plain GET). */
  const lineNow = async (b: PrrRunBody, lineId: string): Promise<PrrLine | null> =>
    b.lines.rows.find(x => x.id === lineId) || (await payrollRunsApi.line(ctx.clientId, b.run.id, lineId).catch(() => null))?.line || null;
  // Everything below reads the run; until it has arrived the one window shows a loader (or the refusal).
  const view = (body: PrrRunBody) => {
    const { run, readiness } = body;
    const year = run.year;
    const typeOf = (code: string) => (types || []).find(x => x.code === code);
    const groupOf = (i: PrrItem) => i.summaryGroup || typeOf(i.code)?.summaryGroup || '';
    // ⛔ What a commit sends is decided in its plan, on the line as the last answer gave it — the item the cell edits
    // may have been added by a commit of this same cell that was still in flight when this one was typed.
    const commitCell = async (l: PrrLine, code: string, groups: string[]) => {
      const key = `${l.id}:${code}`;
      const v = takeDraft(key);
      if (v === undefined) return;
      const sat = parseBaht(v);
      if (sat === null) { failCell(key, v, t('prr_cell_invalid')); return; }
      await commitField(key, v, async b => {
        const now = await lineNow(b, l.id);
        if (!now) return null;
        const it = live(now).filter(i => groups.includes(groupOf(i))).find(i => i.code === code);
        if (it && sat === 0) return rv => payrollRunsApi.removeItem(ctx.clientId, b.run.id, now.id, it.id, { rowVersion: rv });
        if (it && sat !== itemAmount(it)) return rv => payrollRunsApi.patchItem(ctx.clientId, b.run.id, now.id, it.id, { rowVersion: rv, amountSat: sat });
        if (!it && sat > 0) return rv => payrollRunsApi.addItem(ctx.clientId, b.run.id, now.id, { rowVersion: rv, code, amountSat: sat });
        return null;
      });
    };
    const commitDays = async (l: PrrLine) => {
      const key = `${l.id}:days`;
      const v = takeDraft(key);
      if (v === undefined) return;
      const n = Number(String(v).trim());
      // The server's bounds (setDays): above 0, at most PRR_MAX_DAYS, two decimals — anything else stays in the cell.
      if (!(n > 0) || n > PRR_MAX_DAYS || !/^\d+(\.\d{1,2})?$/.test(String(v).trim())) { failCell(key, v, t('prr_days_invalid', { max: PRR_MAX_DAYS })); return; }
      await commitField(key, v, async b => {
        const now = await lineNow(b, l.id);
        return !now || n === Number(now.days) ? null : rv => payrollRunsApi.setDays(ctx.clientId, b.run.id, now.id, { rowVersion: rv, days: n });
      });
    };
    const commitHeader = async (k: 'docDate' | 'payDate' | 'note' | 'ssoOffReason') => {
      const key = `h:${k}`;
      const v = takeDraft(key);
      if (v === undefined) return;
      if ((k === 'docDate' || k === 'payDate') && !isIso(v)) { failCell(key, v, t('prr_date_invalid')); return; }
      if (k === 'ssoOffReason' && !v.trim()) { failCell(key, v, t('prr_sso_off_reason_needed')); return; }
      await commitField(key, v, b => {
        const r = b.run;
        const was = k === 'ssoOffReason' ? r.ssoOffReason : r[k];
        if (v.trim() === String(was || '').trim() && !(k === 'ssoOffReason' && r.ssoMode !== 'off')) return null;
        const patch = k === 'ssoOffReason' ? { ssoMode: 'off' as PrrSsoMode, ssoOffReason: v.trim() } : { [k]: k === 'note' ? v.trim() : v };
        return rv => payrollRunsApi.patch(ctx.clientId, r.id, { rowVersion: rv, ...patch });
      });
    };
    /** The SSO option: 'Not calculated' waits for its reason (typed, so the window is dirty until it is committed). */
    const choose = (v: string) => {
      if (v === 'off') { setLine('h:ssoOffReason', run.ssoOffReason || ''); return; }
      takeDraft('h:ssoOffReason');
      queueWrite(b => (b.run.ssoMode === 'month_rate' ? null : rv => payrollRunsApi.patch(ctx.clientId, b.run.id, { rowVersion: rv, ssoMode: 'month_rate' })), 'edit');
    };
    /** Refresh, Calculate, Approve, Copy — queued behind any write still in flight, and sent only if the readiness the
     *  run has THEN still allows it (`ask`, Approve's one question, is asked at that moment too, on those figures). */
    const act = async (a: 'refresh' | 'calculate' | 'approve' | 'copy', extra: { lineIds?: string[] } = {}, ask?: (b: PrrRunBody) => boolean) => {
      if (busyAct) return;
      setBusyAct(a); setErr('');
      const res = await queueWrite(b => (ask && !ask(b) ? null : rv => payrollRunsApi.action(ctx.clientId, b.run.id, a, { rowVersion: rv, ...extra })), a);
      setBusyAct('');
      if (!res.ok) { setErr(res.err || ''); return; }
      if (a === 'copy' && res.r?.run?.id) onOpenRun(String(res.r.run.id));
    };
    const approve = () => act('approve', {}, b => window.confirm(t('prr_approve_confirm', {
      docNo: b.run.docNo, employees: b.summary.employees, gross: m2(b.summary.income.gross), cost: m2(b.summary.employer.total), net: m2(b.summary.liabilities.net_payable),
    })));
    const reasonDone = async (reason: string): Promise<Done> => {
      if (!win || win.kind !== 'reason') return { ok: false };
      const w = win;
      if (w.act === 'remove') return write(rv => payrollRunsApi.removeLine(ctx.clientId, run.id, String(w.lineId), { rowVersion: rv, reason }));
      return write(rv => payrollRunsApi.action(ctx.clientId, run.id, w.act as 'return' | 'void', { rowVersion: rv, reason }), w.act === 'return' ? 'returnToDraft' : 'void');
    };
    const nav = (m: string, s: string, extra: Record<string, string>) => ctx.setNav(m, s, { run: '', ...extra });
    const fixOf = (r: PrrRefusal): React.ReactNode => {
      if (r.refreshable && readiness.refresh?.ok && !readOnly && r.lineId) {
        return <button style={actBtn} onClick={() => act('refresh', { lineIds: [String(r.lineId)] })} data-fix="refresh"><i className="ti ti-refresh" aria-hidden /> {t('prr_refresh')}</button>;
      }
      const go = (fn: () => void, kind: string) => <button style={linkBtn} onClick={fn} data-fix={kind}>{t('prr_fix')} <i className="ti ti-arrow-right" aria-hidden /></button>;
      if (ITEM_JUMP.has(r.code) && r.itemCode) return go(() => nav('payroll', 'payroll_settings', { focus: `item:${r.itemCode}` }), 'item');
      if (YTD_JUMP.has(r.code) && r.employeeId) return go(() => nav('payroll', 'employees', { emp: String(r.employeeId), etab: 'payroll' }), 'ytd');
      if ((EMP_JUMP.has(r.code) || r.code === 'employee_account_refused') && r.employeeId) return go(() => nav('payroll', 'employees', { emp: String(r.employeeId) }), 'employee');
      if (ROLE_JUMP.has(r.code) && r.role) return go(() => nav('payroll', 'payroll_settings', { focus: `role:${r.role}` }), 'role');
      if (CLAIM_JUMP.has(r.code) && r.runId) return go(() => ctx.setNav('payroll', 'payroll_list', { run: String(r.runId) }), 'claim');
      if (ORDER_JUMP.has(r.code) && runIdOf(r)) return go(() => ctx.setNav('payroll', 'payroll_list', { run: runIdOf(r) }), 'run');
      // An approved run whose basis moved, or whose payroll summary is gone, is fixed by returning it to draft (R7-3) —
      // and so is one of runs that count each other (counted_cycle): calculated again, it counts none that counts it.
      if (RETURN_FIX.has(r.code) && run.status === 'approved' && readiness.returnToDraft?.ok && !readOnly) return go(() => setWin({ kind: 'reason', act: 'return' }), 'return');
      if (LINE_OPEN.has(r.code) && r.lineId) return go(() => setWin({ kind: 'detail', lineId: String(r.lineId) }), 'line');
      return null;
    };
    const advFix = (a: PrrAdvisory): React.ReactNode => {
      const go = (fn: () => void) => <button style={linkBtn} onClick={fn} data-adv-fix={a.code}>{t('prr_fix')} <i className="ti ti-arrow-right" aria-hidden /></button>;
      if ((a.code === 'account_unmapped' || a.code === 'account_refused') && a.role) return go(() => nav('payroll', 'payroll_settings', { focus: `role:${a.role}` }));
      if (a.code === 'item_account_missing' && a.itemCode) return go(() => nav('payroll', 'payroll_settings', { focus: `item:${a.itemCode}` }));
      if (a.code === 'employee_account_refused' && a.employeeId) return go(() => nav('payroll', 'employees', { emp: String(a.employeeId) }));
      return null;
    };
    // The readiness the panel shows: the next step this run can take — Calculate while in draft (or calculated and
    // changed), Approve once calculated, Post once approved, Reverse once posted, Copy once void or reversed. Post and
    // Reverse are shown only once the server answers them (R7: readiness.post / .reverse).
    const step = run.status === 'void' || run.status === 'reversed' ? 'copy' : run.status === 'approved' ? 'post' : run.status === 'posted' ? 'reverse'
      : run.status === 'calculated' && !body.needsRecalc ? 'approve' : 'calculate';
    const stepReady = readiness[step as PrrAction] || { ok: true, refusals: [] };
    const showStep = !readOnly && (((run.status === 'draft' || run.status === 'calculated') && body.canEdit) || run.status === 'void'
      || (run.status === 'reversed' && !!readiness.copy) || (run.status === 'approved' && !!readiness.post) || (run.status === 'posted' && !!readiness.reverse));
    const canCalc = !readOnly && body.canEdit && (run.status === 'draft' || run.status === 'calculated');
    const canApprove = !readOnly && body.canEdit && run.status === 'calculated';
    const canPost = !readOnly && run.status === 'approved' && !!readiness.post;
    // ⛔ Drawn off while a write waits or is in flight: the readiness they show is about to change under them.
    const calcOn = !!readiness.calculate?.ok && !busyAct && saving === 0;
    const approveOn = !!readiness.approve?.ok && !busyAct && saving === 0;
    const postOn = !!readiness.post?.ok && !busyAct && saving === 0;
    // R8: Record payment, only where the run's own readiness.pay allows it (a posted run with something unpaid and an
    // account to pay from) — never for a read-only seat. A refusal other than "nothing left to pay" gets its own panel.
    const canPay = !readOnly && run.status === 'posted' && !!readiness.pay?.ok;
    const payBlocked = !readOnly && run.status === 'posted' && !!readiness.pay && !readiness.pay.ok
      && readiness.pay.refusals.some(r => r.code !== 'nothing_unpaid' && r.code !== 'status');
    const paid = () => { setWin(null); load(); setLogKey(n => n + 1); setPayKey(n => n + 1); onChanged(); };
    const posting = body.posting || null;
    const actions: BarAction[] = readOnly ? [] : [
      ...(readiness.refresh?.ok ? [{ key: 'refresh', label: t('prr_refresh'), icon: 'ti-refresh', title: t('prr_refresh_title'), busy: busyAct === 'refresh', onClick: () => act('refresh') }] : []),
      ...(readiness.edit?.ok && body.canEdit ? [{ key: 'add', label: t('prr_add_employee'), icon: 'ti-user-plus', onClick: () => setWin({ kind: 'add' }) }] : []),
      ...(readiness.returnToDraft?.ok ? [{ key: 'return', label: t('prr_return'), icon: 'ti-arrow-back-up', tone: 'amber' as const, onClick: () => setWin({ kind: 'reason', act: 'return' }) }] : []),
      ...(readiness.void?.ok ? [{ key: 'void', label: t('prr_void'), icon: 'ti-ban', tone: 'amber' as const, onClick: () => setWin({ kind: 'reason', act: 'void' }) }] : []),
      ...(readiness.reverse?.ok && run.status === 'posted' ? [{ key: 'reverse', label: t('prr_reverse'), icon: 'ti-arrow-back', tone: 'amber' as const, onClick: () => setWin({ kind: 'reverse' }) }] : []),
      ...(readiness.copy?.ok ? [{ key: 'copy', label: t('prr_copy'), icon: 'ti-copy', busy: busyAct === 'copy', onClick: () => act('copy') }] : []),
    ];
    const right = (
      <>
        {canCalc && (
          <button style={{ ...btnPrimary, flex: 'none', whiteSpace: 'nowrap', opacity: calcOn ? 1 : 0.5 }} disabled={!calcOn}
            onClick={() => act('calculate')} data-act="calculate" title={readiness.calculate?.ok ? undefined : t('prr_blocked_title')}>
            <i className={busyAct === 'calculate' ? 'ti ti-loader' : 'ti ti-calculator'} aria-hidden /> {t('prr_calculate')}
          </button>
        )}
        {canApprove && (
          <button style={{ ...btnPrimary, flex: 'none', whiteSpace: 'nowrap', opacity: approveOn ? 1 : 0.5 }} disabled={!approveOn}
            onClick={approve} data-act="approve" title={readiness.approve?.ok ? undefined : t('prr_blocked_title')}>
            <i className={busyAct === 'approve' ? 'ti ti-loader' : 'ti ti-circle-check'} aria-hidden /> {t('prr_approve')}
          </button>
        )}
        {canPost && (
          <button style={{ ...btnPrimary, flex: 'none', whiteSpace: 'nowrap', opacity: postOn ? 1 : 0.5 }} disabled={!postOn}
            onClick={() => setWin({ kind: 'post' })} data-act="post" title={readiness.post?.ok ? undefined : t('prr_blocked_title')}>
            <i className="ti ti-book-upload" aria-hidden /> {t('prr_post')}
          </button>
        )}
        {canPay && (
          <button style={{ ...btnPrimary, flex: 'none', whiteSpace: 'nowrap' }} onClick={() => setWin({ kind: 'pay' })} data-act="pay">
            <i className="ti ti-cash" aria-hidden /> {t('prr_record_payment')}
          </button>
        )}
        {/* A posted run's entries — the accrual and, once reversed, its reversal — in the house window (PAY-18, R7-12). Read
            only, so every seat that sees the run sees it; never on an unposted run, which has none. */}
        {(run.status === 'posted' || run.status === 'reversed') && (
          <span data-entries style={{ flex: 'none' }}><DocEntriesBtn ctx={ctx} kind="payroll_run" id={run.id} docNo={run.docNo} /></span>
        )}
        {(PRP_PAYSLIP_STATUSES as readonly string[]).includes(run.status) && (
          <span data-payslips style={{ flex: 'none' }}>
            <DlBtn style={actBtn} title={t('prr_payslips_title')} onRun={() => payrollReportsApi.payslips(ctx.clientId, run.id, run.docNo)}>
              <i className="ti ti-file-type-pdf" aria-hidden /> {t('prr_payslips')}
            </DlBtn>
          </span>
        )}
      </>
    );
    const tabs = (
      <>
        {((run.status === 'posted' || run.status === 'reversed' ? ['lines', 'summary', 'payments', 'log'] : ['lines', 'summary', 'log']) as Tab[]).map(k => (
          <button key={k} data-tab={k} aria-pressed={k === tab} onClick={() => setTab(k)} style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(k === tab ? chipOn : {}) }}>
            {t(`prr_tab_${k}`)}{k === 'lines' ? ` · ${body.lines.total}` : ''}
          </button>
        ))}
      </>
    );
    const hd = (k: string, fallback: string) => drafts[`h:${k}`] ?? sending[`h:${k}`]?.v ?? fallback;
    const offShown = drafts['h:ssoOffReason'] !== undefined || !!sending['h:ssoOffReason'] || run.ssoMode === 'off';
    const headerField = (label: string, view: React.ReactNode, edit: React.ReactNode | null, key?: string) => (
      <div style={{ minWidth: 150 }}>
        <div style={lbl}>{label}</div>
        {writable && edit ? edit : <div style={{ fontSize: 12.5 }}>{view}</div>}
        {key && cellErr[key] && <div style={{ fontSize: 11, color: '#B42318', marginTop: 2 }} data-cell-error={key}>{cellErr[key]}</div>}
      </div>
    );
    const foot = (
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-tertiary)' }} data-run-foot>
        <span>{t('prr_created_by', { who: run.createdByName || '—', when: run.createdAt ? fmtDateTime(run.createdAt) : '—' })}</span>
        {run.calculatedAt ? <span data-calculated-by>{t('prr_calculated_by', { who: run.calculatedByName, when: fmtDateTime(run.calculatedAt) })}</span> : null}
        {run.approvedAt ? <span data-approved-by>{t('prr_approved_by', { who: run.approvedByName, when: fmtDateTime(run.approvedAt) })}</span> : null}
        {saving > 0 && <span data-saving={saving}><i className="ti ti-loader" aria-hidden /> {t('prr_saving')}</span>}
      </div>
    );
    return { foot, docNo: run.docNo, content: (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-run-window={run.id} data-status={run.status} data-row-version={run.rowVersion}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 520px', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }} data-run-header>
              {headerField(t('prr_f_status'), <span style={statusPill(run.status)} data-run-status-chip={run.status}>{runStatusLabel(t, run.status)}</span>, null)}
              {headerField(t('prr_f_type'), runTypeLabel(t, run.runType), null)}
              {headerField(t('prr_f_period'), periodText(run.periodFrom, run.periodTo), null)}
              {headerField(t('prr_f_doc_date'), dmy(run.docDate),
                <input type="date" value={hd('docDate', run.docDate)} onChange={e => setLine('h:docDate', e.target.value)} onBlur={() => commitHeader('docDate')} style={inp} data-h="doc_date" aria-label={t('prr_f_doc_date')} />, 'h:docDate')}
              {headerField(t('prr_f_pay_date'), run.payDate ? dmy(run.payDate) : '—',
                <input type="date" value={hd('payDate', run.payDate)} onChange={e => setLine('h:payDate', e.target.value)} onBlur={() => commitHeader('payDate')} style={inp} data-h="pay_date" aria-label={t('prr_f_pay_date')} />, 'h:payDate')}
              {headerField(t('prr_f_sso'), run.ssoMode === 'off' ? `${t('prr_sso_opt_off')} — ${run.ssoOffReason}` : t('prr_sso_mode_month_rate'),
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <select value={offShown ? 'off' : 'month_rate'} onChange={e => choose(e.target.value)} style={inp} data-h="sso_mode" aria-label={t('prr_f_sso')}>
                    <option value="month_rate">{t('prr_sso_mode_month_rate')}</option><option value="off">{t('prr_sso_opt_off')}</option>
                  </select>
                  {offShown && <input value={hd('ssoOffReason', run.ssoOffReason)} onChange={e => setLine('h:ssoOffReason', e.target.value)} onBlur={() => commitHeader('ssoOffReason')}
                    maxLength={PRR_MAX_TEXT} placeholder={t('prr_sso_off_reason_ph')} style={inp} data-h="sso_off_reason" aria-label={t('prr_f_sso_off_reason')} />}
                </div>, 'h:ssoOffReason')}
              {headerField(t('prr_f_note'), run.note || '—',
                <input value={hd('note', run.note)} onChange={e => setLine('h:note', e.target.value)} onBlur={() => commitHeader('note')} maxLength={PRR_MAX_NOTE} style={inp} data-h="note" aria-label={t('prr_f_note')} />, 'h:note')}
            </div>
            <div style={{ display: 'flex', gap: 10, flex: 'none' }}>
              <div style={{ ...card, minWidth: 170 }} data-total="employer_cost">
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('prr_card_employer_cost')}</div>
                <div style={{ fontSize: 19, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }} data-sat={body.summary.employer.total}>{m2(body.summary.employer.total)}</div>
              </div>
              <div style={{ ...card, minWidth: 170 }} data-total="net">
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('prr_card_net')}</div>
                <div style={{ fontSize: 19, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }} data-sat={body.summary.liabilities.net_payable}>{m2(body.summary.liabilities.net_payable)}</div>
              </div>
            </div>
          </div>
          {err && <div style={errBox} data-run-error>{err}</div>}
          {(posting || run.replacesRunId) && (
            <div style={{ ...card, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }} data-posting={posting ? posting.entryId : ''}>
              {posting && kv(t('prr_f_entry'), <span data-posting-entry={posting.docNo}>{posting.docNo}</span>, 'entry')}
              {posting && kv(t('prr_f_page'), <span data-posting-page={posting.pageNo ?? ''}>{posting.pageNo ?? '—'}</span>, 'page')}
              {posting && kv(t('prr_f_entry_date'), posting.entryDate ? dmy(posting.entryDate) : '—', 'entry_date')}
              {posting && kv(t('prr_f_posted'), <span style={{ fontWeight: 600 }} data-posted-by>{t('prr_posted_by', { who: posting.postedByName || '—', when: posting.postedAt ? fmtDateTime(posting.postedAt) : '—' })}</span>, 'posted')}
              {posting?.reversal && kv(t('prr_f_reversal'), <span data-reversed-by={posting.reversal.docNo}>{t('prr_reversed_by', { docNo: posting.reversal.docNo, page: posting.reversal.pageNo ?? '—' })}</span>, 'reversal')}
              {body.payment && kv(t('prr_f_payment'), (
                <span data-payment-fact={body.payment.status} data-paid-sat={body.payment.paidSat} data-net-sat={body.payment.netSat}>
                  <PayStatusChip status={body.payment.status} t={t} /> {t('prr_paid_of', { paid: m2(body.payment.paidSat), net: m2(body.payment.netSat) })}
                </span>
              ), 'payment')}
              {run.replacesRunId && kv(t('prr_f_replaces'), <ReplacesLink ctx={ctx} runId={run.replacesRunId} />, 'replaces')}
            </div>
          )}
          {!body.canEdit && body.lockReason && (
            <div style={{ ...card, fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8 }} data-lock-reason={body.lockReason}>
              <i className="ti ti-lock" aria-hidden style={{ marginTop: 2 }} />
              <div>{lockText(t, body.lockReason, run.status)}</div>
            </div>
          )}
          {body.needsRecalc && (
            <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E', display: 'flex', gap: 8 }} data-needs-recalc>
              <i className="ti ti-alert-triangle" aria-hidden style={{ marginTop: 2 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <div>{t('prr_needs_recalc')}</div>
                {(body.basisMoved || []).length > 0 && <BasisMovedList ctx={ctx} moves={body.basisMoved} t={t} />}
              </div>
            </div>
          )}
          <div style={{ ...card, padding: 0, overflow: 'visible' }}>
            <PeakToolbar filters={tabs} right={right} actions={actions} />
          </div>
          {showStep && !stepReady.ok && (
            <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }} data-readiness={step}>
              <div style={sectionHead}>{t(`prr_ready_${step}`)}</div>
              {stepReady.refusals.map((r, i) => (
                <div key={`${r.code}:${r.lineId || ''}:${r.itemCode || ''}:${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5 }}
                  data-refusal={r.code} data-employee={r.employeeId || undefined} data-item={r.itemCode || undefined} data-role={r.role || undefined}>
                  <i className="ti ti-circle-x" aria-hidden style={{ color: '#B42318' }} />
                  <div style={{ flex: 1 }}>
                    {whoOf(r) && <b>{whoOf(r)}</b>}{whoOf(r) && r.itemCode ? ' · ' : ''}{r.itemCode && <b>{itemName(r.itemCode)}</b>}{(whoOf(r) || r.itemCode) ? ' — ' : ''}
                    <span data-refusal-text>{refusalText(t, r, year, itemName)}</span>
                  </div>
                  {fixOf(r)}
                </div>
              ))}
            </div>
          )}
          {payBlocked && readiness.pay && (
            <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }} data-readiness="pay">
              <div style={sectionHead}>{t('prr_ready_pay')}</div>
              <PayRefusals refusals={readiness.pay.refusals} t={t} />
            </div>
          )}
          {!readOnly && (run.status === 'draft' || run.status === 'calculated') && body.postAdvisories.length > 0 && (
            <div style={{ ...card, ...amberBox, display: 'flex', flexDirection: 'column', gap: 5 }} data-advisories={body.postAdvisories.length}>
              <div style={{ ...sectionHead, color: '#92400E' }}>{t('prr_adv_head')}</div>
              <div style={{ ...note, marginTop: -4 }}>{t('prr_adv_note')}</div>
              {body.postAdvisories.map((a, i) => (
                <div key={`${a.code}:${a.role || a.itemCode || a.employeeId || ''}:${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: '#92400E' }} data-advisory={a.code}>
                  <div style={{ flex: 1 }}>{advisoryText(t, a, itemName)}</div>{advFix(a)}
                </div>
              ))}
            </div>
          )}
          {tab === 'lines' && (
            <LinesTable body={body} t={t} writable={writable} drafts={drafts} sending={sending} cellErr={cellErr} types={types} groupOf={groupOf} itemName={itemName}
              onDraft={setLine} onCommit={commitCell} onCommitDays={commitDays} onOpen={id => setWin({ kind: 'detail', lineId: id })}
              onRemove={l => setWin({ kind: 'reason', act: 'remove', lineId: l.id, name: l.snap.name })} page={linePage} onPage={setLinePage} />
          )}
          {tab === 'summary' && <SummaryTab body={body} t={t} />}
          {tab === 'payments' && <PaymentsTab ctx={ctx} runId={run.id} readOnly={readOnly} reloadKey={payKey} onReversed={paid} />}
          {tab === 'log' && <LogTab ctx={ctx} runId={run.id} year={year} reloadKey={logKey} t={t} />}
        </div>
        {win?.kind === 'detail' && (
          <LineDetailDialog ctx={ctx} body={body} lineId={win.lineId} writable={writable} types={types} welfareOn={welfareOn} write={write}
            onReason={(l: PrrLine) => setWin({ kind: 'reason', act: 'remove', lineId: l.id, name: l.snap.name })} onClose={() => setWin(null)} />
        )}
        {win?.kind === 'reason' && (
          <ReasonDialog act={win.act} name={win.name || ''} docNo={run.docNo} onSubmit={reasonDone} onClose={() => setWin(null)}
            dependents={win.act === 'remove' ? null : <DependentsNote ctx={ctx} deps={body.dependents || []} t={t} />} />
        )}
        {win?.kind === 'add' && writable && <AddEmployeeDialog ctx={ctx} runId={run.id} write={write} onClose={() => setWin(null)} />}
        {win?.kind === 'post' && canPost && <PostDialog ctx={ctx} body={body} write={write} fix={fixOf} onClose={() => setWin(null)} />}
        {win?.kind === 'reverse' && !readOnly && run.status === 'posted' && <ReverseDialog ctx={ctx} body={body} write={write} fix={fixOf} onClose={() => setWin(null)} />}
        {win?.kind === 'pay' && canPay && <PayDialog ctx={ctx} body={body} onClose={() => setWin(null)} onDone={paid} />}
      </>
    ) };
  };
  const v = body ? view(body) : null;
  return (
    <Dialog fullscreen footer={v ? v.foot : <span />} title={t('prr_win_title')} docNo={v?.docNo} onClose={onClose} dirty={dirty}>
      {v ? v.content : err ? <div style={errBox} data-run-error>{err}</div> : <Loader />}
    </Dialog>
  );
}
function advisoryText(t: TF, a: PrrAdvisory, itemName: (c: string) => string): string {
  const k = `prr_adv_${a.code}`;
  const rule = a.rule ? (t(`prs_rule_${a.rule}`) !== `prs_rule_${a.rule}` ? t(`prs_rule_${a.rule}`) : t('prs_rule_other')) : '';
  const opts = {
    role: a.role ? t(`prs_role_${a.role}`, { form: PND1 }) : '', rule, item: a.itemCode ? itemName(a.itemCode) : '',
    field: a.field ? t(`prr_acct_field_${a.field}`) : '', who: String(a.employeeName || ''),
  };
  return t(k) !== k ? t(k, opts) : t('prr_adv_other');
}

// ── Lines (100 a page): the group cells are typed into in draft, SSO and PND.1 are the server's ──────────
function LinesTable({ body, t, writable, drafts, sending, cellErr, types, groupOf, itemName, onDraft, onCommit, onCommitDays, onOpen, onRemove, page, onPage }: {
  body: PrrRunBody; t: TF; writable: boolean; drafts: Record<string, string>; sending: Record<string, { v: string }>; cellErr: Record<string, string>; types: PrsItem[] | null;
  groupOf: (i: PrrItem) => string; itemName: (c: string, s?: any) => string;
  onDraft: (key: string, v: string) => void; onCommit: (l: PrrLine, code: string, groups: string[]) => void; onCommitDays: (l: PrrLine) => void;
  onOpen: (lineId: string) => void; onRemove: (l: PrrLine) => void; page: number; onPage: (p: number) => void;
}) {
  const { run } = body;
  const daily = run.runType === 'daily';
  const offCycle = run.runType === 'off_cycle';
  // R8: a posted run's lines say what live payments paid on each, and the line's payment status (the server's).
  const payCols = run.status === 'posted';
  const active = (code: string) => !!(types || []).find(x => x.code === code && x.active);
  const rows = body.lines.rows;
  const key = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); };
  // A cell shows what is typed in it, else what it is saving (until the write lands and the run is read again), else
  // the run's own figure.
  const shown = (k: string, fallback: string): string => drafts[k] ?? sending[k]?.v ?? fallback;
  const savingStyle = (k: string): React.CSSProperties => (sending[k] && drafts[k] === undefined ? { color: 'var(--text-tertiary)' } : {});
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-lines>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!rows.length ? <div style={emptyLine} data-lines-empty>{t('prr_lines_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prr_col_employee')}</th><th style={th}>{t('prr_col_paid_to')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{daily ? t('prr_col_days_base') : t('prr_col_base')}</th>
                {CELLS.map(c => <th key={c.group} style={{ ...th, textAlign: 'right' }}>{t(`prr_cell_${c.group}`)}</th>)}
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_gross')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_sso')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{PND1}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_net')}</th>
                {payCols && <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_paid')}</th>}
                {payCols && <th style={th}>{t('prr_col_payment')}</th>}
                <th style={th} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {rows.map(l => {
                const removed = !!Number(l.removed);
                const items = live(l);
                const base = items.filter(i => i.code === 'BASE').reduce((s, i) => s + itemAmount(i), 0);
                const dKey = `${l.id}:days`;
                return (
                  <tr key={l.id} data-line={l.id} data-employee={l.employeeId} data-removed={removed ? '1' : undefined}
                    style={removed ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : undefined}>
                    <td style={{ ...td, minWidth: 190 }}>
                      <button onClick={() => onOpen(l.id)} style={{ ...linkBtn, fontSize: 12.5, textAlign: 'left' }} data-open-line={l.id}>{l.snap.name || l.snap.nameEn || '—'}</button>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{[l.snap.empCode, l.snap.nameEn && l.snap.nameEn !== l.snap.name ? l.snap.nameEn : ''].filter(Boolean).join(' · ')}</div>
                      {removed && <span style={pillGrey} data-line-removed>{t('prr_line_removed')}</span>}
                      {!removed && l.addReason && <span style={{ ...pillGrey, marginTop: 3 }} title={l.addReason} data-line-added>{t('prr_line_added')}</span>}
                      {!removed && (l.masterChanged || []).length > 0 && <span style={{ ...pillAmber, marginTop: 3 }} data-master-changed>{t('prr_master_changed')}</span>}
                      <div><Chips chips={l.chips} year={run.year} t={t} /></div>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }} data-paid-to>
                      {l.snap.payMethod ? t(`prl_opt_pay_method_${l.snap.payMethod}`) : '—'}
                      {l.snap.bankName || l.snap.bankLast4 ? <div style={{ color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{[l.snap.bankName, l.snap.bankLast4 ? `••••${l.snap.bankLast4}` : ''].filter(Boolean).join(' ')}</div> : null}
                    </td>
                    <td style={num} data-base>
                      {offCycle ? '—' : daily ? (
                        <>
                          {writable && !removed ? (
                            <input value={shown(dKey, String(l.days ?? ''))} onChange={e => onDraft(dKey, e.target.value)} onBlur={() => onCommitDays(l)} onKeyDown={key} inputMode="decimal"
                              style={{ ...inp, width: 64, textAlign: 'right', ...savingStyle(dKey), ...(cellErr[dKey] ? amberBox : {}) }} data-days={l.id} data-cell-saving={sending[dKey] ? '1' : undefined} aria-label={t('prr_col_days')} />
                          ) : <span>{l.days ?? '—'}</span>}
                          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>× {mOr(l.snap.dailyRateSat)} = {m2(base)}</div>
                          {cellErr[dKey] && <div style={{ fontSize: 11, color: '#B42318' }} data-cell-error={dKey}>{cellErr[dKey]}</div>}
                        </>
                      ) : m2(base)}
                    </td>
                    {CELLS.map(c => {
                      const inGroup = items.filter(i => c.groups.includes(groupOf(i)));
                      const sum = inGroup.reduce((s, i) => s + itemAmount(i), 0);
                      const k = `${l.id}:${c.code}`;
                      const own = inGroup.filter(i => i.code === c.code);
                      const inline = writable && !removed && active(c.code) && inGroup.length === own.length && own.length <= 1 && (own.length === 0 || own[0].amountSat !== null);
                      return (
                        <td key={c.group} style={num} data-cell-group={c.group}>
                          {inline ? (
                            <input value={shown(k, satText(sum))} onChange={e => onDraft(k, e.target.value)} onBlur={() => onCommit(l, c.code, c.groups)} onKeyDown={key}
                              inputMode="decimal" style={{ ...inp, width: 96, textAlign: 'right', ...savingStyle(k), ...(cellErr[k] ? amberBox : {}) }} data-cell={k} data-cell-saving={sending[k] ? '1' : undefined} aria-label={`${l.snap.name} · ${itemName(c.code)}`} />
                          ) : sum ? <button style={{ ...linkBtn, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} onClick={() => onOpen(l.id)} data-cell-sum={k}>{m2(sum)}</button> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                          {cellErr[k] && <div style={{ fontSize: 11, color: '#B42318', whiteSpace: 'normal', maxWidth: 160 }} data-cell-error={k}>{cellErr[k]}</div>}
                        </td>
                      );
                    })}
                    <td style={num} data-gross={l.grossSat ?? ''}>{mOr(l.grossSat)}</td>
                    <td style={num} data-sso={l.ssoEeSat ?? ''}>{mOr(l.ssoEeSat)}</td>
                    <td style={num} data-wht={l.whtSat ?? ''}>{mOr(l.whtSat)}</td>
                    <td style={{ ...num, fontWeight: 700 }} data-net={l.netSat ?? ''}>{mOr(l.netSat)}</td>
                    {payCols && <td style={num} data-line-paid={l.paidSat ?? ''}>{mOr(l.paidSat)}</td>}
                    {payCols && <td style={td} data-line-payment={l.paymentStatus || ''}>{l.paymentStatus ? <PayStatusChip status={l.paymentStatus} t={t} /> : '—'}</td>}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button style={actBtn} onClick={() => onOpen(l.id)} data-line-detail={l.id}><i className="ti ti-list-details" aria-hidden /> {t('prr_line_open')}</button>
                      {writable && !removed && <button style={{ ...actBtn, marginLeft: 4, color: '#B42318' }} onClick={() => onRemove(l)} data-line-remove={l.id}><i className="ti ti-user-minus" aria-hidden /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={body.lines.total} limit={body.lines.limit || 100} onPage={onPage} />
      <div style={note}>{t(writable ? 'prr_lines_note_edit' : 'prr_lines_note', { form: PND1 })}</div>
    </div>
  );
}

// ── Summary: the four blocks and the three invariants, as the server worked them out (PAY-10) ─────────────
function SummaryTab({ body, t }: { body: PrrRunBody; t: TF }) {
  const s = body.summary;
  const inv = body.invariants;
  const blocks = Object.keys(PRR_SUMMARY_KEYS) as PrrSummaryBlock[];
  const ticks: [string, boolean][] = [['net', !!inv?.netOk], ['employer', !!inv?.employerCostOk], ['liabilities', !!inv?.liabilitiesOk && !!inv?.balanced]];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-summary>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {blocks.map(b => (
          <section key={b} style={card} data-summary-block={b}>
            <div style={sectionHead}>{t(`prr_sum_${b}`)}</div>
            <table style={table}>
              <tbody>
                {PRR_SUMMARY_KEYS[b].map(k => {
                  const total = k === 'total' || k === 'gross';
                  return (
                    <tr key={k} data-summary-key={`${b}.${k}`} data-sat={s[b]?.[k] ?? 0}>
                      <td style={{ ...td, ...(total ? { fontWeight: 800 } : {}) }}>{t(`prr_sum_${b}_${k}`, { form: PND1 })}</td>
                      <td style={{ ...num, ...(total ? { fontWeight: 800 } : {}) }}>{m2(s[b]?.[k] ?? 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}
      </div>
      <section style={card} data-invariants={inv?.ok ? 'ok' : 'broken'}>
        <div style={sectionHead}>{t('prr_inv_head')}</div>
        {ticks.map(([k, good]) => (
          <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '3px 0' }} data-invariant={k} data-ok={good ? '1' : '0'}>
            <i className={good ? 'ti ti-circle-check' : 'ti ti-circle-x'} style={{ color: good ? '#166534' : '#B42318' }} aria-hidden />
            {t(`prr_inv_${k}`)}
          </div>
        ))}
        <div style={{ ...note, marginTop: 6 }}>{t('prr_inv_note', { debit: m2(inv?.debitSat ?? 0), credit: m2(inv?.creditSat ?? 0) })}</div>
      </section>
    </div>
  );
}

// ── Log (100 a page): every action and every field that moved, translated ────────────────────────────────
const MONEY_FIELDS = new Set(['amount_satang', 'rate_satang', 'sso_ee_override', 'wht_override']);
const DATE_FIELDS = new Set(['doc_date', 'pay_date']);
/** R7: the run events that name an entry (the accrual, its reversal, a voucher acknowledged as not this pay). */
const ENTRY_EVENTS = new Set(['post', 'reverse', 'dup_ack']);
/** R8: a payment's events name its number (and its reversal's entry); a remittance's, its form and month. The detail is
 *  the payment or the remittance as the server logged it — read for those few fields only, anything else ignored. */
const PAY_EVENTS = new Set(['pay', 'pay_reverse']);
const REMIT_EVENTS = new Set(['remit', 'remit_reverse']);
const payEventDoc = (d: any): string => {
  const x = d?.payment || d || {};
  return [x.payNo, d?.reversalDocNo || x.reversal?.docNo || x.entryDocNo].filter(Boolean).map(String).join(' · ');
};
const remitEventDoc = (d: any): string => {
  const x = d?.remittance || d || {};
  const form = x.kind === 'pnd1' ? PND1 : x.kind === 'sso' ? SSO_SHORT : '';
  return [form, x.year && x.month ? mmYyyy(Number(x.year), Number(x.month)) : '', x.entryDocNo || ''].filter(Boolean).join(' ');
};
/** The entry such an event names: the reversal's number on a reverse, the voucher's on an acknowledgement, the accrual's on a post. */
const entryDocOf = (d: any): string => String(d?.reversalDocNo || d?.voucherDocNo || d?.entryDocNo || d?.docNo || '');
/** A filing row as a snapshot event logs it ({wage, override} in BAHT, payroll_events old/new_value) — wage · override. */
const snapValue = (v: any): string => {
  let o: any = v;
  if (typeof v === 'string') { try { o = JSON.parse(v); } catch { return v; } }
  return o && typeof o === 'object' ? `${money(Number(o.wage) || 0)} · ${o.override === null || o.override === undefined ? '—' : money(Number(o.override))}` : '';
};
function LogTab({ ctx, runId, year, reloadKey, t }: { ctx: AwCtx; runId: string; year: number; reloadKey: number; t: TF }) {
  const itemName = useItemName();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrrPage<PrrEvent> | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollRunsApi.events(ctx.clientId, runId, page).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, runId, page, reloadKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const has = (k: string) => t(k) !== k;
  const value = (f: string, v: any): string => {
    if (v === null || v === undefined || v === '') return t('prr_blank');
    if (MONEY_FIELDS.has(f)) return m2(v);
    if (DATE_FIELDS.has(f)) return isIso(String(v)) ? dmy(String(v)) : String(v);
    if (f === 'status' && (PRR_RUN_STATUSES as readonly string[]).includes(String(v))) return runStatusLabel(t, String(v));
    if (f === 'sso_mode' && has(`prr_sso_mode_${v}`)) return t(`prr_sso_mode_${v}`);
    if (f === 'code') return itemName(String(v));
    if (f === 'removed') return Number(v) ? t('prr_yes') : t('prr_no');
    if (f === 'emp_snapshot' || f === 'base_claim' || f === 'supporting_doc_id') return t('prr_changed');
    return String(v);
  };
  if (err) return <div style={errBox}>{err}</div>;
  if (!data) return <Loader />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-log>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!data.rows.length ? <div style={emptyLine}>{t('prr_log_none')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('prr_log_when')}</th><th style={th}>{t('prr_log_change')}</th><th style={th}>{t('prr_log_who')}</th></tr></thead>
            <tbody>
              {data.rows.map(r => r.entity === 'snapshot' ? (
                // R7: what a Post or a Reverse did to one ภ.ง.ด.1 / สปส. row of the month (package B's ops) — the form, the
                // person, and the row's wage · override before and after (the reason column holds the op's door, not a person's).
                <tr key={r.id} data-event={r.id} data-action={r.action} data-entity="snapshot" data-field={r.field || undefined}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</td>
                  <td style={td}>
                    <b>{has(`prr_snap_${r.action}`) ? t(`prr_snap_${r.action}`) : t('prr_ev_action_other')}</b>{r.detail?.name ? ` · ${String(r.detail.name)}` : ''}
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {r.field === 'sso' ? SSO_FORM : PND1}: <span style={{ color: 'var(--text-tertiary)' }} data-old>{r.oldValue ? snapValue(r.oldValue) : t('prr_blank')}</span> → <span data-new>{r.newValue ? snapValue(r.newValue) : t('prr_blank')}</span>
                    </div>
                  </td>
                  <td style={td}>{r.actorName}</td>
                </tr>
              ) : (
                <tr key={r.id} data-event={r.id} data-action={r.action} data-field={r.field || undefined}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</td>
                  <td style={td}>
                    <b>{has(`prr_ev_action_${r.action}`) ? t(`prr_ev_action_${r.action}`) : t('prr_ev_action_other')}</b>
                    {r.employeeName ? ` · ${r.employeeName}` : ''}
                    {ENTRY_EVENTS.has(r.action) && entryDocOf(r.detail) ? <span data-event-entry> · {entryDocOf(r.detail)}</span> : null}
                    {PAY_EVENTS.has(r.action) && payEventDoc(r.detail) ? <span data-event-payment> · {payEventDoc(r.detail)}</span> : null}
                    {REMIT_EVENTS.has(r.action) && remitEventDoc(r.detail) ? <span data-event-remittance> · {remitEventDoc(r.detail)}</span> : null}
                    {r.field && (
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        {has(`prr_ev_field_${r.field}`) ? t(`prr_ev_field_${r.field}`) : t('prr_ev_field_other')}: <span style={{ color: 'var(--text-tertiary)' }} data-old>{value(r.field, r.oldValue)}</span> → <span data-new>{value(r.field, r.newValue)}</span>
                      </div>
                    )}
                    {r.reason && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }} data-reason>{t('prr_log_reason', { reason: r.reason })}</div>}
                  </td>
                  <td style={td}>{r.actorName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />
      <div style={note} data-log-year={year}>{t('prr_log_note')}</div>
    </div>
  );
}

// ── One line, read-only: its items, the SSO card, the PND.1 card (R6-7: a nested Dialog, Esc closes it alone) ──
function LineDetailDialog({ ctx, body, lineId, writable, types, welfareOn, write, onReason, onClose }: {
  ctx: AwCtx; body: PrrRunBody; lineId: string; writable: boolean; types: PrsItem[] | null; welfareOn: boolean; write: Writer;
  onReason: (l: PrrLine) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const [d, setD] = useState<PrrLineDetail | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<{ kind: 'item'; item: PrrItem | null } | { kind: 'adjust' } | null>(null);
  useEffect(() => {
    let alive = true;
    payrollRunsApi.line(ctx.clientId, body.run.id, lineId).then(x => { if (alive) { setD(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, body.run.id, lineId, reload, body.run.rowVersion]);   // eslint-disable-line react-hooks/exhaustive-deps
  const run = body.run;
  const l = d?.line;
  const c = d?.calcJson || null;
  const canWrite = writable && !!l && !Number(l.removed);
  // ⛔ An item is changed here only while the server would take the change: a monthly BASE, or an item whose type is
  // still among those a line may carry (usableTypes). One switched off since — or a welfare item with the fund now off —
  // can only be removed; the row says why and jumps to it in Payroll settings. Until the types are read, no pencil.
  const usable = types ? usableTypes(types, welfareOn) : null;
  const editable = (i: PrrItem) => (i.code === 'BASE' ? run.runType !== 'daily' : !!usable?.some(x => x.code === i.code));
  const offCode = (i: PrrItem) => (WELFARE_GROUPS.includes(i.summaryGroup) && !welfareOn ? 'welfare_off' : 'item_unknown');
  const removeItem = async (i: PrrItem) => {
    if (!l || !window.confirm(t('prr_item_remove_confirm', { item: itemName(i.code, i) }))) return;
    const r = await write(rv => payrollRunsApi.removeItem(ctx.clientId, run.id, l.id, i.id, { rowVersion: rv }));
    if (!r.ok) setErr(r.err || ''); else setReload(n => n + 1);
  };
  return (
    <Dialog width={920} title={l ? `${l.snap.name || l.snap.nameEn}` : t('prr_line_title')} docNo={l ? [l.snap.empCode, run.docNo].filter(Boolean).join(' · ') : run.docNo} onClose={onClose}>
      {err && <div style={{ ...errBox, marginBottom: 8 }} data-line-error>{err}</div>}
      {!d || !l ? (err ? null : <Loader />) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-line-detail={l.id}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}><Chips chips={l.chips} year={run.year} t={t} /></div>
            {/* R9: this person's payslip, while the run has payslips and the line is live (a removed line has none). */}
            {(PRP_PAYSLIP_STATUSES as readonly string[]).includes(run.status) && !Number(l.removed) && (
              <span data-payslip style={{ flex: 'none' }}>
                <DlBtn style={actBtn} title={t('prr_payslip_title')} onRun={() => payrollReportsApi.payslips(ctx.clientId, run.id, run.docNo, { lineId: l.id, empCode: l.snap.empCode })}>
                  <i className="ti ti-file-type-pdf" aria-hidden /> {t('prr_payslip')}
                </DlBtn>
              </span>
            )}
          </div>
          <section style={card} data-line-items>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ ...sectionHead, marginBottom: 0, flex: 1 }}>{t('prr_items_head')}</div>
              {canWrite && <button style={actBtn} onClick={() => setForm({ kind: 'item', item: null })} data-add-item><i className="ti ti-plus" aria-hidden /> {t('prr_add_item')}</button>}
              {canWrite && <button style={actBtn} onClick={() => setForm({ kind: 'adjust' })} data-adjust><i className="ti ti-adjustments" aria-hidden /> {t('prr_adjust')}</button>}
              {canWrite && <button style={{ ...actBtn, color: '#B42318' }} onClick={() => onReason(l)} data-remove-line><i className="ti ti-user-minus" aria-hidden /> {t('prr_remove_line')}</button>}
            </div>
            {!live(l).length ? <div style={emptyLine}>{t('prr_items_none')}</div> : (
              <table style={table}>
                <thead><tr><th style={th}>{t('prr_col_item')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_qty_rate')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_amount')}</th><th style={th}>{t('prr_col_note')}</th><th style={th} aria-hidden /></tr></thead>
                <tbody>
                  {live(l).map(i => (
                    <tr key={i.id} data-item-row={i.code}>
                      <td style={td}>
                        {itemName(i.code, i)} <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{i.code}</span>
                        {canWrite && usable && i.code !== 'BASE' && !editable(i) && (
                          <div style={{ fontSize: 11.5, color: '#92400E', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }} data-item-off={i.code}>
                            <span>{refusalText(t, { code: offCode(i), itemCode: i.code }, run.year, itemName)}</span>
                            <button style={linkBtn} onClick={() => ctx.setNav('payroll', 'payroll_settings', { focus: `item:${i.code}`, run: '' })} data-item-off-fix={i.code}>{t('prr_fix')} <i className="ti ti-arrow-right" aria-hidden /></button>
                          </div>
                        )}
                      </td>
                      <td style={num}>{i.qty !== null && i.qty !== undefined && i.rateSat !== null && i.rateSat !== undefined ? `${i.qty} × ${m2(i.rateSat)}` : '—'}</td>
                      <td style={num} data-item-sat={itemAmount(i)}>{m2(itemAmount(i))}</td>
                      <td style={td}>{i.note || ''}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {canWrite && editable(i) && <button style={actBtn} onClick={() => setForm({ kind: 'item', item: i })} data-edit-item={i.code}><i className="ti ti-pencil" aria-hidden /></button>}
                        {canWrite && i.code !== 'BASE' && <button style={{ ...actBtn, marginLeft: 4, color: '#B42318' }} onClick={() => removeItem(i)} data-remove-item={i.code}><i className="ti ti-trash" aria-hidden /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          {!c ? <div style={{ ...card, ...emptyLine }} data-not-calculated>{t('prr_not_calculated')}</div> : (
            <>
              <SsoCard c={c} t={t} />
              <Pnd1Card c={c} year={run.year} t={t} />
            </>
          )}
        </div>
      )}
      {form?.kind === 'item' && l && (
        <ItemDialog ctx={ctx} body={body} line={l} item={form.item} types={types || []} welfareOn={welfareOn} write={write}
          onClose={() => setForm(null)} onDone={() => { setForm(null); setReload(n => n + 1); }} />
      )}
      {form?.kind === 'adjust' && l && (
        <AdjustDialog ctx={ctx} body={body} line={l} write={write} onClose={() => setForm(null)} onDone={() => { setForm(null); setReload(n => n + 1); }} />
      )}
    </Dialog>
  );
}
const kv = (label: string, value: React.ReactNode, attr?: string) => (
  <div style={{ minWidth: 150 }} data-kv={attr}>
    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);
function SsoCard({ c, t }: { c: PrrCalcJson; t: TF }) {
  const s = c.ssoCalc || ({} as PrrCalcJson['ssoCalc']);
  return (
    <section style={card} data-sso-card data-source={s.source}>
      <div style={sectionHead}>{t('prr_sso_head', { form: SSO_FORM })}</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {kv(t('prr_sso_base_month'), m2(s.baseMonthSat), 'base')}
        {kv(t('prr_sso_rate'), s.rate !== undefined ? `${s.rate}% · ${money(s.cap ?? 0)}` : '—', 'rate')}
        {kv(t('prr_sso_month'), m2(s.sMonthSat), 'month')}
        {kv(t('prr_sso_others'), m2(s.otherEeSat), 'others')}
        {kv(t('prr_sso_ee'), m2(s.eeSat), 'ee')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }} data-sso-source={s.source}>{ssoSourceText(t, s.source, c.ssoOffReason || '')}</div>
    </section>
  );
}
function Pnd1Card({ c, year, t }: { c: PrrCalcJson; year: number; t: TF }) {
  const ded = c.ded || ({} as PrrCalcJson['ded']);
  const months = c.ytd?.months || [];
  const op = c.ytd?.opening;
  // A month's source in the words the employee's Payroll tab uses for it (package D, payrollText.ts) — one wording.
  const src = (m: { month: number; source: string }) => ytdSourceLabel(t, m.source, mmYyyy(year, m.month), PND1);
  return (
    <section style={card} data-pnd1-card data-wht-source={c.whtSource}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ ...sectionHead, marginBottom: 0 }}>{PND1} · {t('prr_pnd1_head')}</div>
        <span style={pillGrey} data-projected-chip title={t('prr_projected_hint')}>{t('prr_projected_chip', { method: c.method })}</span>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        {kv(t('prr_pnd1_this_run'), <span data-line-wht={c.whtSat}>{m2(c.whtSat)}</span>, 'wht')}
        {kv(t('prr_pnd1_comparison'), <span data-line-comparison={c.comparisonSat}>{m2(c.comparisonSat)}</span>, 'comparison')}
        {kv(t('prr_pnd1_source'), whtSourceText(t, c.whtSource), 'source')}
      </div>
      <div style={{ ...note, marginBottom: 8 }}>{t('prr_pnd1_comparison_note', { form: PND1 })}</div>
      <table style={table} data-ytd>
        <thead><tr><th style={th}>{t('prr_ytd_month')}</th><th style={th}>{t('prr_ytd_source')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_ytd_income')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_ytd_withheld')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_ytd_sso')}</th></tr></thead>
        <tbody>
          {op && (
            <tr data-ytd-opening>
              <td style={td}>{t('prr_ytd_opening_row', { through: mmYyyy(year, op.throughMonth) })}</td><td style={td}>{t('prr_ytd_opening', { through: mmYyyy(year, op.throughMonth) })}</td>
              <td style={num}>{m2(op.incomeSat)}</td><td style={num}>{m2(op.withheldSat)}</td><td style={num}>{m2(op.ssoSat)}</td>
            </tr>
          )}
          {months.map(m => (
            <tr key={m.month} data-ytd-month={m.month} data-ytd-source={m.source}>
              <td style={td}>{mmYyyy(year, m.month)}</td>
              <td style={td} data-ytd-source-text>{src(m)}</td>
              <td style={num}>{m.source === 'opening' ? '—' : m2(m.incomeSat)}</td><td style={num}>{m.source === 'opening' ? '—' : m2(m.withheldSat)}</td><td style={num}>{m.source === 'opening' ? '—' : m2(m.ssoSat)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }} data-pnd1-working>
        {kv(t('prr_pnd1_n'), c.n, 'n')}
        {kv(t('prr_pnd1_a_reg'), m2(c.aRegSat), 'a_reg')}
        {kv(t('prr_pnd1_a'), m2(c.aSat), 'a')}
        {kv(t('prr_pnd1_ded_expense'), m2(ded.expenseSat), 'ded_expense')}
        {kv(t('prr_pnd1_ded_allowances'), m2(ded.allowancesSat), 'ded_allowances')}
        {kv(t('prr_pnd1_ded_emp'), m2(ded.empAllowSat), 'ded_emp')}
        {kv(t('prr_pnd1_ded_sso'), m2(ded.ssoSat), 'ded_sso')}
        {kv(t('prr_pnd1_t_a_reg'), m2(Math.round(c.tARegSat)), 't_a_reg')}
        {kv(t('prr_pnd1_t_a'), m2(Math.round(c.tASat)), 't_a')}
        {kv(t('prr_pnd1_w_o'), m2(c.wOSat), 'w_o')}
        {kv(t('prr_pnd1_w_month'), m2(c.wMonthSat), 'w_month')}
      </div>
    </section>
  );
}

// ── An item on a line (a small form): quantity × rate, or an amount; a note ───────────────────────────────
function ItemDialog({ ctx, body, line, item, types, welfareOn, write, onClose, onDone }: {
  ctx: AwCtx; body: PrrRunBody; line: PrrLine; item: PrrItem | null; types: PrsItem[]; welfareOn: boolean; write: Writer; onClose: () => void; onDone: () => void;
}) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const run = body.run;
  const [code, setCode] = useState(item?.code || '');
  const [mode, setMode] = useState<'amount' | 'qty'>(item && item.amountSat === null ? 'qty' : 'amount');
  const [amount, setAmount] = useState(item && item.amountSat !== null ? satText(Number(item.amountSat)) : '');
  const [qty, setQty] = useState(item?.qty !== null && item?.qty !== undefined ? String(item.qty) : '');
  const [rate, setRate] = useState(item?.rateSat !== null && item?.rateSat !== undefined ? satText(Number(item.rateSat)) : '');
  const [noteText, setNoteText] = useState(item?.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  // ⛔ What the server would refuse is not offered: BASE is the line's own (never added; never on an off-cycle run,
  // R6-19), an inactive item cannot be used, and the welfare items only while the client's fund is on (R6-25).
  const choices = usableTypes(types, welfareOn);
  const amt = parseBaht(amount);
  const q = Number(qty);
  const r = parseBaht(rate);
  const valid = mode === 'amount' ? amt !== null && amt > 0 : /^\d+(\.\d{1,2})?$/.test(qty.trim()) && q > 0 && r !== null && r > 0;
  // An item already on the line is saved only while it could still be added (a monthly BASE is the line's own).
  const ready = !!code && valid && (item?.code === 'BASE' || choices.some(x => x.code === code));
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const payload = mode === 'amount' ? { amountSat: amt as number, qty: null, rateSat: null } : { amountSat: null, qty: q, rateSat: r as number };
    const res = await write(rv => (item
      ? payrollRunsApi.patchItem(ctx.clientId, run.id, line.id, item.id, { rowVersion: rv, ...payload, note: noteText.trim() })
      : payrollRunsApi.addItem(ctx.clientId, run.id, line.id, { rowVersion: rv, code, ...payload, note: noteText.trim() })));
    setBusy(false);
    if (!res.ok) { setErr(res.err || ''); return; }
    setDirty(false); onDone();
  };
  return (
    <Dialog width={540} title={t(item ? 'prr_item_edit_title' : 'prr_item_add_title')} docNo={line.snap.name} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-check" label={t('prr_item_save')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-item-form>
        {err && <div style={errBox} data-item-error>{err}</div>}
        <div>
          <label htmlFor="prr-it-code" style={lbl}>{t('prr_col_item')}</label>
          {item ? <div style={{ fontSize: 12.5, fontWeight: 700 }}>{itemName(item.code, item)}</div> : (
            <select id="prr-it-code" value={code} onChange={e => ed(setCode, e.target.value)} style={field} data-it="code">
              <option value="">{t('prr_choose')}</option>
              {choices.map(x => <option key={x.code} value={x.code}>{itemName(x.code, x)} · {x.code}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
          <label><input type="radio" checked={mode === 'amount'} onChange={() => ed(setMode, 'amount' as const)} data-it="mode_amount" /> {t('prr_item_by_amount')}</label>
          <label><input type="radio" checked={mode === 'qty'} onChange={() => ed(setMode, 'qty' as const)} data-it="mode_qty" /> {t('prr_item_by_qty')}</label>
        </div>
        {mode === 'amount' ? (
          <div>
            <label htmlFor="prr-it-amount" style={lbl}>{t('prr_col_amount')}</label>
            <input id="prr-it-amount" value={amount} onChange={e => ed(setAmount, e.target.value)} inputMode="decimal" style={field} data-it="amount" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label htmlFor="prr-it-qty" style={lbl}>{t('prr_item_qty')}</label><input id="prr-it-qty" value={qty} onChange={e => ed(setQty, e.target.value)} inputMode="decimal" style={field} data-it="qty" /></div>
            <div><label htmlFor="prr-it-rate" style={lbl}>{t('prr_item_rate')}</label><input id="prr-it-rate" value={rate} onChange={e => ed(setRate, e.target.value)} inputMode="decimal" style={field} data-it="rate" /></div>
          </div>
        )}
        <div>
          <label htmlFor="prr-it-note" style={lbl}>{t('prr_col_note')}</label>
          <input id="prr-it-note" value={noteText} onChange={e => ed(setNoteText, e.target.value)} maxLength={PRR_MAX_TEXT} style={field} data-it="note" />
        </div>
        <div style={note}>{t('prr_item_note')}</div>
      </div>
    </Dialog>
  );
}

// ── An authorised adjustment (a small form): SSO in whole baht, PND.1 an amount — each with its reason ───────
// ⛔ The SSO box is offered only on a line whose employee is in social security as the line froze them (snap.inSso —
// what the engine reads, and what the server refuses an SSO figure by, adjust_invalid on ssoEeSat): a figure typed
// for anyone else would be credited to SSO payable by the accrual and missing from the month's SSO filing (R7 review
// F1). Such a line never sends one; a figure left on it from before is named, and Save clears it (ssoEeSat null).
function AdjustDialog({ ctx, body, line, write, onClose, onDone }: { ctx: AwCtx; body: PrrRunBody; line: PrrLine; write: Writer; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const run = body.run;
  const member = !!line.snap?.inSso;
  const staleSso = !member && line.ssoEeOverrideSat !== null && line.ssoEeOverrideSat !== undefined;
  const [sso, setSso] = useState(member && line.ssoEeOverrideSat !== null && line.ssoEeOverrideSat !== undefined ? String(Number(line.ssoEeOverrideSat) / 100) : '');
  const [ssoReason, setSsoReason] = useState(line.ssoReason || '');
  const [wht, setWht] = useState(line.whtOverrideSat !== null && line.whtOverrideSat !== undefined ? satText(Number(line.whtOverrideSat)) || '0.00' : '');
  const [whtReason, setWhtReason] = useState(line.whtReason || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const ssoTyped = member && sso.trim() !== '';
  const ssoOk = !ssoTyped || /^\d+$/.test(sso.trim());
  const whtSat = wht.trim() === '' ? null : parseBaht(wht);
  const whtOk = wht.trim() === '' || whtSat !== null;
  const ready = (dirty || staleSso) && ssoOk && whtOk && (!ssoTyped || !!ssoReason.trim()) && (wht.trim() === '' || !!whtReason.trim());
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const res = await write(rv => payrollRunsApi.adjust(ctx.clientId, run.id, line.id, {
      rowVersion: rv, ssoEeSat: ssoTyped ? Number(sso.trim()) * 100 : null, ssoReason: ssoTyped ? ssoReason.trim() : '',
      whtSat: wht.trim() === '' ? null : whtSat, whtReason: wht.trim() === '' ? '' : whtReason.trim(),
    }));
    setBusy(false);
    if (!res.ok) { setErr(res.err || ''); return; }
    setDirty(false); onDone();
  };
  return (
    <Dialog width={560} title={t('prr_adjust_title')} docNo={line.snap.name} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-check" label={t('prr_adjust_save')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-adjust-form>
        {err && <div style={errBox} data-adjust-error>{err}</div>}
        <div style={note}>{t('prr_adjust_note', { form: PND1 })}</div>
        {member ? (
          <div>
            <label htmlFor="prr-adj-sso" style={lbl}>{t('prr_adjust_sso')}</label>
            <input id="prr-adj-sso" value={sso} onChange={e => ed(setSso, e.target.value)} inputMode="numeric" style={{ ...field, ...(!ssoOk ? amberBox : {}) }} data-adj="sso" />
            {!ssoOk && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-adj-sso-bad>{t('prr_adjust_sso_whole')}</div>}
            {sso.trim() !== '' && <input value={ssoReason} onChange={e => ed(setSsoReason, e.target.value)} maxLength={PRR_MAX_TEXT} placeholder={t('prr_reason_ph')} style={{ ...field, marginTop: 6, ...(!ssoReason.trim() ? amberBox : {}) }} data-adj="sso_reason" aria-label={t('prr_reason')} />}
          </div>
        ) : (
          <div style={{ ...note, ...(staleSso ? { ...amberBox, color: '#92400E', padding: '6px 8px', borderRadius: 6 } : {}) }} data-adj-sso-none={staleSso ? 'stale' : 'none'}>
            {staleSso ? t('prr_adjust_sso_stale', { amount: m2(line.ssoEeOverrideSat) }) : t('prr_adjust_sso_none')}
          </div>
        )}
        <div>
          <label htmlFor="prr-adj-wht" style={lbl}>{t('prr_adjust_wht', { form: PND1 })}</label>
          <input id="prr-adj-wht" value={wht} onChange={e => ed(setWht, e.target.value)} inputMode="decimal" style={{ ...field, ...(!whtOk ? amberBox : {}) }} data-adj="wht" />
          {wht.trim() !== '' && <input value={whtReason} onChange={e => ed(setWhtReason, e.target.value)} maxLength={PRR_MAX_TEXT} placeholder={t('prr_reason_ph')} style={{ ...field, marginTop: 6, ...(!whtReason.trim() ? amberBox : {}) }} data-adj="wht_reason" aria-label={t('prr_reason')} />}
        </div>
      </div>
    </Dialog>
  );
}

// ── A reason (a small form): Return to draft, Void, Remove an employee from the run ─────────────────────────
function ReasonDialog({ act, name, docNo, onSubmit, onClose, dependents }: {
  act: 'return' | 'void' | 'remove'; name: string; docNo: string; onSubmit: (reason: string) => Promise<Done>; onClose: () => void;
  /** Return and Void: the approved runs this one's going will flag (DependentsNote), shown before the reason is asked. */
  dependents?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const ready = !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const res = await onSubmit(reason.trim());
    setBusy(false);
    if (!res.ok) { setErr(res.err || ''); return; }
    setDirty(false); onClose();
  };
  return (
    <Dialog width={520} title={t(`prr_reason_title_${act}`)} docNo={act === 'remove' ? `${name} · ${docNo}` : docNo} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={act === 'void' ? 'ti ti-ban' : act === 'remove' ? 'ti ti-user-minus' : 'ti ti-arrow-back-up'} label={t(`prr_reason_go_${act}`)} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-reason-form={act}>
        {err && <div style={errBox} data-reason-error>{err}</div>}
        <div style={note}>{t(`prr_reason_note_${act}`)}</div>
        {dependents}
        <label htmlFor="prr-reason" style={lbl}>{t('prr_reason')}</label>
        <textarea id="prr-reason" value={reason} onChange={e => ed(setReason, e.target.value)} rows={3} maxLength={PRR_MAX_TEXT} style={field} data-reason-text />
      </div>
    </Dialog>
  );
}

// ── Add an employee (a small form): those inside the run's criteria, and the others — who need a reason ───────
function AddEmployeeDialog({ ctx, runId, write, onClose }: { ctx: AwCtx; runId: string; write: Writer; onClose: () => void }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrrCandidates | null>(null);
  const [pick, setPick] = useState<{ id: string; outside: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.candidates(ctx.clientId, runId, page).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, runId, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const idOf = (c: PrrCandidate) => String(c.employeeId);
  const ready = !!pick && (!pick.outside || !!reason.trim());
  const submit = async () => {
    if (!ready || busy || !pick) return;
    setBusy(true); setErr('');
    const res = await write(rv => payrollRunsApi.addLine(ctx.clientId, runId, { rowVersion: rv, employeeId: pick.id, ...(pick.outside ? { addReason: reason.trim() } : {}) }));
    setBusy(false);
    if (!res.ok) { setErr(res.err || ''); return; }
    setDirty(false); onClose();
  };
  const list = (rows: PrrCandidate[], outside: boolean) => (!rows.length ? <div style={{ ...emptyLine, padding: 12 }}>{t('prr_add_none')}</div> : (
    <table style={table}>
      <tbody>
        {rows.map(c => (
          <tr key={idOf(c)} data-candidate={idOf(c)} data-outside={outside ? '1' : '0'} data-why={c.why || undefined}>
            <td style={{ ...td, width: 28 }}>
              {/* ⛔ A base salary another live run holds is never offered (the server answers claim_taken): no pick. */}
              {c.addable !== false && <input type="radio" name="prr-add-pick" checked={pick?.id === idOf(c)} onChange={() => ed(setPick, { id: idOf(c), outside })} aria-label={c.name} data-pick={idOf(c)} />}
            </td>
            <td style={td}><b>{c.name}</b><div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{[c.empCode, c.empType ? t(`prl_type_${c.empType}`) : ''].filter(Boolean).join(' · ')}</div></td>
            <td style={{ ...td, fontSize: 12, color: 'var(--text-secondary)' }}>{[c.startDate ? dmy(c.startDate) : '', c.terminationDate ? `→ ${dmy(c.terminationDate)}` : ''].filter(Boolean).join(' ')}</td>
            <td style={{ ...td, fontSize: 12, color: '#92400E' }}>{outside && c.why ? whyText(t, c) : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  ));
  const total = Math.max(data?.inCriteria.total || 0, data?.outside.total || 0);
  return (
    <Dialog width={760} title={t('prr_add_title')} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-user-plus" label={t('prr_add_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-add-form>
        {err && <div style={errBox} data-add-error>{err}</div>}
        {!data ? (err ? null : <Loader />) : (
          <>
            <div style={sectionHead}>{t('prr_add_in', { n: data.inCriteria.total })}</div>
            {list(data.inCriteria.rows, false)}
            <div style={sectionHead}>{t('prr_add_out', { n: data.outside.total })}</div>
            {list(data.outside.rows, true)}
            <Pagination page={page} total={total} limit={data.limit || 100} onPage={setPage} />
          </>
        )}
        {pick?.outside && (
          <div>
            <label htmlFor="prr-add-reason" style={lbl}>{t('prr_add_reason')}</label>
            <textarea id="prr-add-reason" value={reason} onChange={e => ed(setReason, e.target.value)} rows={2} maxLength={PRR_MAX_TEXT} style={{ ...field, ...(!reason.trim() ? amberBox : {}) }} data-add-reason />
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── Batch 10 close-out: what an approved run was charged against that has moved, and who counts this run ──────────
// GET /runs/:id answers both (services/payrollRuns.ts — basisMoved and dependents, the R11 sweep): the run window
// names them, each run a jump through the readiness panel's ORDER_JUMP way (Payroll list ?run=<id> by ctx.setNav,
// which asks first when something is typed).

/** Another run of this client, opened the way ORDER_JUMP's Fix opens one. */
function RunJump({ ctx, runId, docNo }: { ctx: AwCtx; runId: string; docNo: string }) {
  return (
    <button type="button" style={linkBtn} onClick={() => ctx.setNav('payroll', 'payroll_list', { run: runId })} data-run-jump={runId}>
      {docNo} <i className="ti ti-arrow-right" aria-hidden />
    </button>
  );
}

/** Under the needsRecalc banner of an approved or posted run: each line it was charged against that has moved since —
 *  whose line, which run (its state now) and why, in PRR_BASIS_MOVE_WHYS' words. */
function BasisMovedList({ ctx, moves, t }: { ctx: AwCtx; moves: PrrBasisMove[]; t: TF }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-basis-moved={moves.length}>
      <div style={{ fontWeight: 700 }}>{t('prr_basis_moved_head')}</div>
      {moves.map((m, i) => (
        <div key={`${m.lineId}:${m.runId}:${m.why}:${i}`} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}
          data-basis-move={m.runId} data-why={m.why} data-employee={m.employeeId}>
          <b>{m.employeeName || '—'}</b>
          <span aria-hidden>·</span>
          <RunJump ctx={ctx} runId={m.runId} docNo={m.docNo} />
          {!!m.status && <span style={statusPill(m.status)}>{runStatusLabel(t, m.status)}</span>}
          {(PRR_BASIS_MOVE_WHYS as readonly string[]).includes(m.why) && <span data-basis-why>— {t(`prr_basis_why_${m.why}`)}</span>}
        </div>
      ))}
    </div>
  );
}

/** Before Return to draft or Void: the month's approved runs charged against this one, one row per run (whose lines),
 *  each a jump — they will need recalculating once this one goes. Nothing when none counts it. */
function DependentsNote({ ctx, deps, t }: { ctx: AwCtx; deps: PrrDependent[]; t: TF }) {
  const runs: { runId: string; docNo: string; status: string; names: string[] }[] = [];
  for (const d of deps) {
    let r = runs.find(x => x.runId === d.runId);
    if (!r) { r = { runId: d.runId, docNo: d.docNo, status: d.status, names: [] }; runs.push(r); }
    if (d.employeeName && !r.names.includes(d.employeeName)) r.names.push(d.employeeName);
  }
  if (!runs.length) return null;
  return (
    <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E', display: 'flex', flexDirection: 'column', gap: 5 }} data-dependents={runs.length}>
      <div>{t('prr_dependents_head')}</div>
      {runs.map(r => (
        <div key={r.runId} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }} data-dependent={r.runId}>
          <RunJump ctx={ctx} runId={r.runId} docNo={r.docNo} />
          {!!r.status && <span style={statusPill(r.status)}>{runStatusLabel(t, r.status)}</span>}
          {r.names.length > 0 && <span>— {r.names.join(', ')}</span>}
        </div>
      ))}
    </div>
  );
}

// ── R7: the run a copy replaces, Post and Reverse ────────────────────────────────────────────────────────

/** "Replaces PR-…" on a copy: the number read off that run (a plain GET), and a jump to it through the shell, which asks
 *  first when a cell holds typing. */
function ReplacesLink({ ctx, runId }: { ctx: AwCtx; runId: string }) {
  const { t } = useTranslation();
  const [docNo, setDocNo] = useState('');
  useEffect(() => {
    let alive = true;
    payrollRunsApi.get(ctx.clientId, runId).then(b => { if (alive) setDocNo(String(b.run.docNo || '')); }).catch(() => { /* the jump still opens it */ });
    return () => { alive = false; };
  }, [ctx.clientId, runId]);
  return (
    <button style={{ ...linkBtn, fontSize: 13 }} onClick={() => ctx.setNav('payroll', 'payroll_list', { run: runId })} data-replaces={runId}>
      {docNo || t('prr_open_run')} <i className="ti ti-arrow-right" aria-hidden />
    </button>
  );
}

/** An account line of the accrual (or its reversal) in the reader's language, with what the line is (ENTRY_ROLES). */
function useAccountWords() {
  const { t, i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return {
    name: (l: { nameTh: string; nameEn: string }) => (thai ? l.nameTh || l.nameEn : l.nameEn || l.nameTh),
    role: (role: string): string => {
      const k = `prr_entry_role_${role}`;
      return t(k) !== k ? t(k, { form: PND1 }) : '';
    },
  };
}
/** One entry's lines by account, Dr before Cr — R8's payment and remittance dialogs (PayrollPayments.tsx,
 *  PayrollRemittances.tsx) draw theirs with it too, so an entry reads one way wherever it is previewed. */
export function EntryLines({ lines, totalSat, balanced, t }: { lines: PrrEntryLine[]; totalSat: number; balanced?: boolean; t: TF }) {
  const w = useAccountWords();
  const dr = lines.reduce((n, l) => n + (Number(l.drSat) || 0), 0);
  const cr = lines.reduce((n, l) => n + (Number(l.crSat) || 0), 0);
  return (
    <table style={table} data-entry-lines={lines.length}>
      <thead><tr><th style={th}>{t('prr_col_account')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_dr')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_cr')}</th></tr></thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={`${l.side}:${l.accountId}:${i}`} data-entry-line={`${l.side}:${l.code}`} data-role={l.role} data-dr={Number(l.drSat) || 0} data-cr={Number(l.crSat) || 0}>
            <td style={td}>
              <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', paddingLeft: l.side === 'cr' ? 22 : 0 }}>{l.code}</span> {w.name(l)}
              {w.role(l.role) && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', paddingLeft: l.side === 'cr' ? 22 : 0 }}>{w.role(l.role)}</div>}
            </td>
            <td style={num}>{Number(l.drSat) ? m2(l.drSat) : ''}</td>
            <td style={num}>{Number(l.crSat) ? m2(l.crSat) : ''}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr data-entry-total={totalSat}>
          <td style={{ ...td, fontWeight: 800 }}>{t('prr_post_total')}</td>
          <td style={{ ...num, fontWeight: 800 }}>{m2(dr)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{m2(cr)}</td>
        </tr>
        {balanced !== undefined && (
          <tr><td style={{ ...td, borderBottom: 'none' }} colSpan={3} data-balanced={balanced ? '1' : '0'}>
            <span style={balanced ? pillGreen : pillRed}><i className={balanced ? 'ti ti-check' : 'ti ti-alert-triangle'} aria-hidden /> {t(balanced ? 'prr_post_balanced' : 'prr_post_unbalanced')}</span>
          </td></tr>
        )}
      </tfoot>
    </table>
  );
}
/** One form's change: its total before → after, and how many rows are written, taken over, filled in and added by hand. */
function FilingSummary({ st, t }: { st: PrrSnapshotPlan; t: TF }) {
  const n = (table: string, ops: string[]) => st.rows.filter(r => r.table === table && ops.includes(r.op)).length;
  const part = (kind: 'pnd1' | 'sso', table: string, form: string) => (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }} data-filing={kind}
      data-before={st.totals[kind].beforeSat} data-after={st.totals[kind].afterSat}>
      <b style={{ minWidth: 70 }}>{form}</b>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}><span style={{ color: 'var(--text-tertiary)' }}>{m2(st.totals[kind].beforeSat)}</span> → <b>{m2(st.totals[kind].afterSat)}</b></span>
      <span style={{ color: 'var(--text-secondary)' }} data-filing-counts={`${n(table, ['write', 'update'])}:${n(table, ['take'])}:${n(table, ['fill'])}:${st.manualRows[kind]}`}>
        {t('prr_post_filing_counts', { written: n(table, ['write', 'update']), taken: n(table, ['take']), restored: n(table, ['restore']), removed: n(table, ['remove', 'unfill']), filled: n(table, ['fill']), manual: st.manualRows[kind] })}
      </span>
    </div>
  );
  return <>{part('pnd1', 'pnd1_lines', PND1)}{part('sso', 'sso_lines', SSO_FORM)}</>;
}
/** The rows a Post or a Reverse changes, one per person and form: wage · override before → after, and "edited by hand"
 *  where a person (or the wage sync) changed the row after it was written. */
function SnapRowsTable({ rows, ops, t }: { rows: PrrSnapRow[]; ops: string[]; t: TF }) {
  const shown = rows.filter(r => ops.includes(r.op));
  const afterOf = (r: PrrSnapRow) => r.after || rows.find(x => x.table === r.table && x.empId === r.empId && (x.op === 'write' || x.op === 'update'))?.after || null;
  const v = (x: { wage: number; override: number | null } | null) => (x ? `${money(Number(x.wage) || 0)} · ${x.override === null || x.override === undefined ? '—' : money(Number(x.override))}` : '—');
  if (!shown.length) return <div style={{ ...emptyLine, padding: 10 }}>{t('prr_none_dash')}</div>;
  return (
    <table style={table}>
      <thead><tr><th style={th}>{t('prr_col_change')}</th><th style={th}>{t('prr_col_form')}</th><th style={th}>{t('prr_col_employee')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_wage_override')}</th></tr></thead>
      <tbody>
        {shown.map((r, i) => (
          <tr key={`${r.table}:${r.op}:${r.rowId || r.empId}:${i}`} data-snap-row={r.op} data-table={r.table} data-emp={r.empId}>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>{t(`prr_snap_${r.op}`) !== `prr_snap_${r.op}` ? t(`prr_snap_${r.op}`) : r.op}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.table === 'sso_lines' ? SSO_FORM : PND1}</td>
            <td style={td}>{r.name || '—'}{r.before?.edited && <span style={{ ...pillAmber, marginLeft: 6 }} data-edited>{t('prr_row_edited')}</span>}</td>
            <td style={{ ...num, whiteSpace: 'nowrap' }}><span style={{ color: 'var(--text-tertiary)' }}>{v(r.before)}</span> → {v(r.op === 'take' || r.op === 'update' ? afterOf(r) : r.after)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
const RefusalList = ({ refusals, year, t, itemName, fix }: { refusals: PrrRefusal[]; year: number; t: TF; itemName: (c: string) => string; fix: (r: PrrRefusal) => React.ReactNode }) => (
  <div style={{ ...card, ...redBox, display: 'flex', flexDirection: 'column', gap: 5 }} data-dialog-refusals={refusals.length}>
    {refusals.map((r, i) => (
      <div key={`${r.code}:${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: '#B42318' }} data-refusal={r.code} data-role={r.role || undefined}>
        <i className="ti ti-circle-x" aria-hidden />
        <div style={{ flex: 1 }}>
          {whoOf(r) && <b>{whoOf(r)}</b>}{whoOf(r) && r.itemCode ? ' · ' : ''}{r.itemCode && <b>{itemName(r.itemCode)}</b>}{(whoOf(r) || r.itemCode) ? ' — ' : ''}
          <span data-refusal-text>{refusalText(t, r, year, itemName)}</span>
        </div>
        {fix(r)}
      </div>
    ))}
  </div>
);
const AdvisoryList = ({ advisories, t }: { advisories: PrrPostAdvisory[]; t: TF }) => (
  <div style={{ ...card, ...amberBox, display: 'flex', flexDirection: 'column', gap: 4 }}>
    {advisories.map((a, i) => <div key={`${a.code}:${i}`} style={{ fontSize: 12.5, color: '#92400E' }} data-post-advisory={a.code}><i className="ti ti-alert-triangle" aria-hidden /> {postAdvText(t, a)}</div>)}
  </div>
);

// ── Post (a wide card; its form is the ticks) ────────────────────────────────────────────────────────────
// Everything on it is the server's dry run (GET …/post-preview — the plan the POST runs again, R7-1): the accrual's
// lines by account to the satang, what the month's ภ.ง.ด.1 and SSO rows become, and every live voucher of the period
// that debits an account this run will debit (gate 4, R7-7) — one tick each. Post is pressed only with no refusal,
// every voucher ticked and, where filing rows are replaced, the takeover ticked for the rows shown (the tick holds the
// digest it was given: rows that move under it untick it). It sends the ticks and that digest; a 409 saying they moved
// reads the plan again and says so, never guesses.
function PostDialog({ ctx, body, write, fix, onClose }: { ctx: AwCtx; body: PrrRunBody; write: Writer; fix: (r: PrrRefusal) => React.ReactNode; onClose: () => void }) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const words = useAccountWords();
  const run = body.run;
  const [pv, setPv] = useState<PrrPostPreview | null>(null);
  const [reload, setReload] = useState(0);
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const [takeAck, setTakeAck] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [changed, setChanged] = useState(false);
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.postPreview(ctx.clientId, run.id).then(x => { if (alive) { setPv(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, run.id, run.rowVersion, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const st = pv?.statutory || null;
  const dups = pv?.duplicates || [];
  const replaced = (st?.rows || []).filter(r => r.op === 'take' || r.op === 'update');
  const needTake = !!pv?.needsTakeoverAck;
  const takeOk = !needTake || (!!st && takeAck === st.digest);
  const ready = !!pv && !!pv.entry && !pv.refusals.length && dups.every(d => acks[d.entryId]) && takeOk;
  const submit = async () => {
    if (!ready || busy || !pv) return;
    setBusy(true); setErr(''); setChanged(false);
    const res = await write(rv => payrollRunsApi.post(ctx.clientId, run.id, {
      rowVersion: rv, dupAck: dups.filter(d => acks[d.entryId]).map(d => d.entryId), takeoverAck: needTake && takeOk, takeoverDigest: st?.digest || '',
    }), 'post');
    setBusy(false);
    if (res.ok) { setDirty(false); onClose(); return; }
    if (res.code === 'takeover_changed' || res.code === 'dup_unacked') setChanged(true); else setErr(res.err || '');
    setReload(n => n + 1);
  };
  const e = pv?.entry || null;
  return (
    <Dialog width={920} title={t('prr_post_title')} docNo={run.docNo} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-book-upload" label={t('prr_post_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-post-form>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ ...note, flex: 1 }}>{t('prr_post_note', { form: PND1 })}</div>
          <button style={actBtn} onClick={() => setReload(n => n + 1)} disabled={busy} data-post-refresh><i className="ti ti-refresh" aria-hidden /> {t('prr_refresh')}</button>
        </div>
        {changed && <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E' }} data-post-changed>{t('prr_post_changed')}</div>}
        {err && <div style={errBox} data-post-error>{err}</div>}
        {!pv ? (err ? null : <Loader />) : (
          <>
            {pv.refusals.length > 0 && <RefusalList refusals={pv.refusals} year={run.year} t={t} itemName={itemName} fix={fix} />}
            {pv.advisories.length > 0 && <AdvisoryList advisories={pv.advisories} t={t} />}
            {e && (
              <section style={card} data-post-entry>
                <div style={sectionHead}>{t('prr_post_entry_head')}</div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 8 }}>
                  {kv(t('prr_f_entry_date'), <span data-entry-date={e.date}>{dmy(e.date)}</span>, 'date')}
                  {kv(t('prr_f_book'), statutoryBookName(e.book) || e.book, 'book')}
                  {kv(t('prr_col_doc_no'), e.docNo, 'doc_no')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }} data-entry-narration>{e.narration}</div>
                <EntryLines lines={e.lines} totalSat={e.totalSat} balanced={e.balanced} t={t} />
              </section>
            )}
            {st && (
              <section style={card} data-post-filings>
                <div style={sectionHead}>{t('prr_post_filings_head')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}><FilingSummary st={st} t={t} /></div>
                <div style={{ fontSize: 12, fontWeight: 700, margin: '6px 0 4px' }}>{t('prr_post_takeover_head')}</div>
                <SnapRowsTable rows={st.rows} ops={['take', 'update']} t={t} />
                <div style={{ fontSize: 12, fontWeight: 700, margin: '10px 0 4px' }}>{t('prr_post_roster_head')}</div>
                <div style={{ fontSize: 12.5 }} data-post-roster={st.uncovered.length}>
                  {st.uncovered.length ? st.uncovered.map(u => `${u.name} (${m2(u.wageSat)})`).join(', ') : t('prr_none_dash')}
                </div>
                {needTake && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>
                    <input type="checkbox" checked={!!st && takeAck === st.digest} onChange={ev => ed(setTakeAck, ev.target.checked ? st.digest : '')} data-takeover-tick />
                    {t('prr_post_takeover_tick', { count: replaced.length })}
                  </label>
                )}
              </section>
            )}
            <section style={card} data-post-dups={dups.length}>
              <div style={sectionHead}>{t('prr_post_dups_head')}</div>
              {!dups.length ? <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('prr_post_dups_none')}</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={note}>{t('prr_post_dups_note')}</div>
                  {dups.map(d => (
                    <div key={d.entryId} style={{ ...card, ...(acks[d.entryId] ? {} : amberBox), padding: 10 }} data-dup={d.entryId} data-dup-total={d.totalSat}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{d.docNo}</b><span>{dmy(d.date)}</span>
                        <span style={d.status === 'posted' ? pillGreen : pillAmber}>{t(d.status === 'posted' ? 'aw_jl_status_posted' : 'aw_jl_status_draft')}</span>
                        <span style={{ marginLeft: 'auto', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{m2(d.totalSat)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 6px' }}>
                        {d.lines.map((l, i) => <div key={`${l.code}:${i}`} data-dup-line={l.code}>{l.code} {words.name(l)} · {m2(l.drSat)}</div>)}
                      </div>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, fontWeight: 600 }}>
                        <input type="checkbox" checked={!!acks[d.entryId]} onChange={ev => ed(setAcks, { ...acks, [d.entryId]: ev.target.checked })} data-dup-tick={d.entryId} />
                        {t('prr_post_dup_tick', { docNo: d.docNo })}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Reverse (a card; its form is the reason) ─────────────────────────────────────────────────────────────
// The server's dry run again (GET …/reverse-preview): the reversing entry — the accrual's lines swapped, dated as it is,
// in its period — and what the month's ภ.ง.ด.1 and SSO rows go back to (taken rows restored, payroll rows removed, fill
// rows withdrawn). Reverse needs a reason and no refusal; a later posted run that counted this one is named with a
// jump to it (reverse that one first, R7-23).
function ReverseDialog({ ctx, body, write, fix, onClose }: { ctx: AwCtx; body: PrrRunBody; write: Writer; fix: (r: PrrRefusal) => React.ReactNode; onClose: () => void }) {
  const { t } = useTranslation();
  const itemName = useItemName();
  const run = body.run;
  const [pv, setPv] = useState<PrrReversePreview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.reversePreview(ctx.clientId, run.id).then(x => { if (alive) { setPv(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(prrErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, run.id, run.rowVersion]);   // eslint-disable-line react-hooks/exhaustive-deps
  const ready = !!pv && !pv.refusals.length && !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const res = await write(rv => payrollRunsApi.reverse(ctx.clientId, run.id, { rowVersion: rv, reason: reason.trim() }), 'reverse');
    setBusy(false);
    if (!res.ok) { setErr(res.err || ''); return; }
    setDirty(false); onClose();
  };
  const st = pv?.statutory || null;
  return (
    <Dialog width={640} title={t('prr_reverse_title')} docNo={run.docNo} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-arrow-back" label={t('prr_reverse_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-reverse-form>
        <div style={note}>{t('prr_reverse_note', { form: PND1 })}</div>
        {err && <div style={errBox} data-reverse-error>{err}</div>}
        {!pv ? (err ? null : <Loader />) : (
          <>
            {pv.refusals.length > 0 && <RefusalList refusals={pv.refusals} year={run.year} t={t} itemName={itemName} fix={fix} />}
            {pv.advisories.length > 0 && <AdvisoryList advisories={pv.advisories} t={t} />}
            {pv.reversal && (
              <section style={card} data-reverse-entry>
                <div style={sectionHead}>{t('prr_reverse_entry_head', { date: dmy(pv.reversal.date) })}</div>
                <EntryLines lines={pv.reversal.lines} totalSat={pv.reversal.totalSat} t={t} />
              </section>
            )}
            {st && (
              <section style={card} data-reverse-filings>
                <div style={sectionHead}>{t('prr_post_filings_head')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}><FilingSummary st={st} t={t} /></div>
                <SnapRowsTable rows={st.rows} ops={['restore', 'remove', 'unfill', 'fill', 'take', 'write', 'update']} t={t} />
              </section>
            )}
          </>
        )}
        {/* (Batch 11 W4 P9, LB-8) Reversing a posted run flags the approved runs charged against it for recalculation, as
            Return and Void do — said here too, before the reason, each a jump. */}
        <DependentsNote ctx={ctx} deps={body.dependents || []} t={t} />
        <label htmlFor="prr-rev-reason" style={lbl}>{t('prr_reason')}</label>
        <textarea id="prr-rev-reason" value={reason} onChange={ev => ed(setReason, ev.target.value)} rows={3} maxLength={PRR_MAX_TEXT} style={{ ...field, ...(!reason.trim() ? amberBox : {}) }} data-reverse-reason />
      </div>
    </Dialog>
  );
}
